import React, { useEffect, useState } from 'react';
import UpgradePromptsPanel from './admin/billing/UpgradePromptsPanel';
import BillingEmailsPanel from './admin/billing/BillingEmailsPanel';
import FailedAttemptsCard from './admin/billing/FailedAttemptsCard';
import ManualPaymentCard from './admin/billing/ManualPaymentCard';
import { ChangePlanModal, describePlanChange } from './admin/billing/ChangePlanModal';
import { apiService, TrialSegment } from '../services/apiService';
import { money } from '../utils/money';
import { DataTable, panelGet, panelPost } from './admin/kit';
import {
  EMPTY_TAG_FILTER, LIFECYCLE_LABEL, TagFilter, TagFilterValue, UserTagCells, tagFilterActive, tagText,
  toLifecycle,
} from './admin/tags/shared';

/**
 * /admin — billing management. Reuses the normal Cosmos login: the page reads
 * the stored session (localStorage user_id + access_token) and the backend
 * decides admin-ness by the signed-in account's email against billing_admins.
 * There is no separate admin login; not signed in → pointer to the main app.
 */

type Gate = 'checking' | 'signed_out' | 'denied' | 'ok';
type Tab = 'users' | 'trials' | 'plans' | 'promos' | 'prompts' | 'coupons' | 'payments' | 'razorpay' | 'gateway' | 'emails';
const TAB_IDS: Tab[] = ['users', 'trials', 'plans', 'promos', 'prompts', 'coupons', 'payments', 'razorpay', 'gateway', 'emails'];
const isTab = (v: unknown): v is Tab => typeof v === 'string' && (TAB_IDS as string[]).includes(v);

const PER_PAGE = 10;

const inr = (paise: number | null | undefined) =>
  paise == null ? '—' : `₹${(paise / 100).toLocaleString('en-IN')}`;
const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
/** Date with the time — for logs where "opened, then kept, 40 seconds later"
 *  is the whole story and a bare date would collapse it. Keeps the year:
 *  cancellations trickle in, so the newest 200 span well over twelve months. */
const fmtWhen = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

/** The cancellation/event tables hold both exits, told apart by a `kind`
 *  column the server stores as 'trial' | 'subscription'. Shown as
 *  "membership" because that is the word the customer-facing tab uses; an
 *  absent kind is a row written before the column existed, which was always
 *  a trial. */
const kindLabel = (kind: string | null | undefined) =>
  kind === 'subscription' ? 'membership' : 'trial';

/** How a trial row came to exist, in the words the panel uses elsewhere. The
 *  mandate flag wins over the source string: a trial that will charge at its
 *  end is the one that matters, whatever screen created it. */
const trialSource = (t: any) =>
  t.source === 'trial_admin' ? 'granted by admin'
  : (t.source === 'trial_mandate' || t.is_mandate) ? 'autopay trial'
  : t.source === 'trial' ? 'trial'
  : (t.source || '—');

/** A row's lifecycle (explorer / voyager paid / assigned / trial / trial
 *  lapsed / prospect · invited, user_tags.decorate) with the account's custom
 *  tags — the shared pill every admin tab uses. It replaced the old state
 *  badge, which named only the two trial states and voyager_assigned. A
 *  payload from before decoration still carries `state` (billing_service
 *  words), which the pill maps; it cannot tell a prospect from an explorer. */
const withLifecycle = (u: any) => (u?.lifecycle ? u : { ...u, lifecycle: u?.state || null });
/** The same cell as plain text, for the Trials CSV and column filter. Only
 *  `lifecycle`: a trial row's `state` is the per-trial vocabulary (active /
 *  lapsed / converted …), not the account's. */
const lifecycleText = (u: any) => {
  const lc = toLifecycle(u?.lifecycle);
  return [u?.lifecycle_label || LIFECYCLE_LABEL[lc] || lc, tagText(u)].filter(Boolean).join(' · ');
};

/** GET billing/overview with the standard tag filter (lifecycle, tags,
 *  tags_all, not_tags, invite_sent, signed_in). apiService's method only
 *  takes search/page/per_page, so a filtered read goes through the same
 *  request path and bearer headers with the extra params appended — the
 *  unfiltered read stays on the public method. */
const fetchBillingOverview = (userId: string, search: string, page: number, perPage: number,
                              tagParams: Record<string, string>) => {
  const extra = Object.entries(tagParams).filter(([, v]) => v)
    .map(([k, v]) => `&${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('');
  if (!extra) return apiService.adminBillingOverview(userId, search, page, perPage);
  const svc = apiService as any;
  return svc.makeRequest(
    `/api/v1/admin/billing/overview?user_id=${encodeURIComponent(userId)}`
    + `&search=${encodeURIComponent(search)}&page=${page}&per_page=${perPage}${extra}`,
    { headers: svc.billingHeaders() }) as ReturnType<typeof apiService.adminBillingOverview>;
};

/** The Trials table's lifecycle filter, in the server's words. 'converted'
 *  is a trial row superseded by a payment; 'lapsed' ran out unpaid. */
const TRIAL_SEGMENTS: TrialSegment[] = ['all', 'active', 'lapsed', 'converted', 'cancelled'];
const SEGMENT_LABEL: Record<TrialSegment, string> = {
  all: 'All', active: 'Active', lapsed: 'Lapsed', converted: 'Converted', cancelled: 'Cancelled',
};

/** Who filed a cancellation. The server stores an e-mail in cancelled_by —
 *  the customer's own when they used the sheet, the admin's when the Users
 *  tab's Cancel did it — so "the same address as the row's user" is the
 *  whole test. Rows from before the column existed have nothing. */
const cancelledBy = (c: any) => {
  const by = String(c.cancelled_by || '').trim();
  if (!by) return '—';
  return c.email && by.toLowerCase() === String(c.email).toLowerCase() ? 'user' : by;
};

/** "{INR: 900000, USD: 4900}" (minor units, as billing_payments stores them)
 *  → "₹9,000 · $49". A sum across currencies would be a lie, so they stay
 *  side by side. */
const amountsByCurrency = (m: Record<string, number> | null | undefined) => {
  const parts = Object.entries(m || {}).map(([cur, minor]) => money(minor, cur));
  return parts.length ? parts.join(' · ') : '—';
};

/** The Razorpay tab's three lists, in the gateway's own words. */
type GwKind = 'subscriptions' | 'payments' | 'refunds';
/** Razorpay's subscription statuses, in the order its dashboard filter
 *  offers them. 'all' is ours. */
const GW_SUB_STATUSES = ['active', 'authenticated', 'pending', 'halted', 'cancelled', 'completed', 'all'];
/** Which gateway statuses still mean "this mandate can charge" — the ones
 *  worth a Cancel button. Mirrors _LIVE_MANDATE_SQL on the server. */
const GW_LIVE = new Set(['active', 'authenticated', 'pending', 'halted', 'created']);
/** A status pill in the colours the rest of the panel uses: green is money
 *  moving as intended, amber is waiting on the customer, red is a mandate
 *  Razorpay has given up retrying, grey is over. */
const gwTone = (s: string | null | undefined) =>
  s === 'active' || s === 'captured' || s === 'processed'
    ? 'bg-green-50 text-green-700 border-green-200'
  : s === 'authenticated' || s === 'pending' || s === 'created' || s === 'authorized'
    ? 'bg-amber-50 text-amber-700 border-amber-200'
  : s === 'halted' || s === 'failed'
    ? 'bg-red-50 text-red-700 border-red-200'
  : 'bg-grayscale-200 text-grayscale-1100 border-grayscale-300';
/** Something on a gateway row that names the person, for the e-mail filter.
 *  Razorpay's own fields first, then whatever our row knows. */
const gwHaystack = (r: any) => [
  r.id, r.payment_id, r.customer_id, r.email, r.contact,
  r.customer?.email, r.customer?.contact, r.customer?.name,
  r.notes?.email, r.our_row?.email, r.our_row?.payer_email, r.our_row?.user_id,
  r.notes_user?.email, r.plan?.name, r.plan?.code, r.description,
].filter(Boolean).join(' ').toLowerCase();

const Card: React.FC<{ title?: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-xl border border-grayscale-300 bg-white p-4">
    {title && <div className="text-sm font-bold text-grayscale-1300 font-grotesk mb-3">{title}</div>}
    {children}
  </div>
);

/** One number with its caption. The page had no stat tile before — its only
 *  counts sat in the header as text — and the trial funnel is read as four
 *  figures against each other, which a sentence cannot do. */
const Stat: React.FC<{
  label: string; value: number | null | undefined; hint?: string;
  /** A line under the caption for what the count alone cannot say — the
   *  refunds tile is a count, but the number that matters is the money. */
  extra?: React.ReactNode;
}> =
  ({ label, value, hint, extra }) => (
    <div className="rounded-xl border border-grayscale-300 bg-white px-4 py-3" title={hint}>
      <div className="text-2xl font-bold text-grayscale-1300 font-grotesk leading-tight">
        {value == null ? '—' : value.toLocaleString()}
      </div>
      <div className="text-xs text-grayscale-1100 mt-0.5">{label}</div>
      {extra && <div className="text-xs text-grayscale-1200 mt-1">{extra}</div>}
    </div>
  );

const Btn: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { subtle?: boolean }> =
  ({ subtle, className = '', ...rest }) => (
    <button {...rest} className={
      (subtle
        ? 'py-1.5 px-3 rounded-full text-xs border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200 '
        : 'py-1.5 px-3 rounded-full text-xs font-medium bg-primary-500 text-grayscale-100 hover:bg-primary-700 ')
      + 'disabled:opacity-40 ' + className} />
  );

/** Two-step destructive confirm, the same shape as admin/kit's DangerBtn:
 *  the first click arms it, a second within 5s fires. Kept local because the
 *  kit's takes no `disabled`, and the refund dialog needs the button dead
 *  until a reason is typed — money leaving without a why is the thing the
 *  refunds log exists to prevent. */
const DangerBtn: React.FC<{
  onConfirm: () => void; children: React.ReactNode; disabled?: boolean; title?: string;
}> = ({ onConfirm, children, disabled, title }) => {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 5000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button type="button" disabled={disabled} title={title}
      onClick={() => { if (armed) { setArmed(false); onConfirm(); } else setArmed(true); }}
      className={'py-1.5 px-3 rounded-full text-xs font-medium whitespace-nowrap border '
        + 'disabled:opacity-40 '
        + (armed
          ? 'bg-red-600 text-white border-red-600 hover:bg-red-700'
          : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100')}>
      {armed ? 'Click again to confirm' : children}
    </button>
  );
};

/** A plain centred dialog. This page had none, which is why several decisions
 *  were being taken through window.prompt() — fine for a yes/no, useless for
 *  "pick one of 190 things and see what it costs". */
const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> =
  ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
       onClick={onClose}>
    <div className="bg-white rounded-xl border border-grayscale-300 shadow-xl w-full max-w-lg
      max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-grayscale-200">
        <div className="text-sm font-bold text-grayscale-1300 font-grotesk">{title}</div>
        <button onClick={onClose} title="Close"
          className="w-7 h-7 rounded-full hover:bg-grayscale-200 text-grayscale-1100">×</button>
      </div>
      <div className="p-4 overflow-y-auto">{children}</div>
    </div>
  </div>
);

/** Pick the billing country for one account.
 *
 *  The currency sits beside every country because that is the actual
 *  consequence of the choice — the admin is not picking a place, they are
 *  picking what the customer gets charged in, and the region each country
 *  resolves to is what decides it.
 */
const CountryPicker: React.FC<{
  email: string; current: string; countries: any[];
  onPick: (country: string) => void; onClose: () => void;
}> = ({ email, current, countries, onPick, onClose }) => {
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? countries.filter((c: any) =>
        String(c.country).toLowerCase().includes(needle)
        || String(c.currency).toLowerCase().includes(needle)
        || String(c.region_name || '').toLowerCase().includes(needle))
    : countries;
  return (
    <Modal title={`Billing country — ${email}`} onClose={onClose}>
      <div className="text-xs text-grayscale-1100 mb-3">
        Decides the currency this account is charged in. Clearing it hands the
        decision back to geo detection.
      </div>
      <input autoFocus value={q} onChange={e => setQ(e.target.value)}
        placeholder="Search country, currency or region…"
        className="w-full h-9 px-3 mb-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
      <div className="border border-grayscale-300 rounded-lg divide-y divide-grayscale-200
        max-h-[45vh] overflow-y-auto">
        <button onClick={() => onPick('')}
          className={'w-full text-left px-3 py-2 text-sm hover:bg-primary-50 '
            + (!current ? 'bg-primary-50 font-medium' : '')}>
          <span className="text-grayscale-1200">No override</span>
          <span className="text-grayscale-1100 text-xs"> — detect from where they are</span>
        </button>
        {shown.map((c: any) => (
          <button key={c.match} onClick={() => onPick(c.country)}
            className={'w-full text-left px-3 py-2 text-sm hover:bg-primary-50 flex '
              + 'items-center justify-between gap-3 '
              + (String(current).toLowerCase() === String(c.country).toLowerCase()
                 ? 'bg-primary-50 font-medium' : '')}>
            <span className="truncate">
              {c.country}
              {!c.known && <span className="text-grayscale-1100 text-xs"> · on an account already</span>}
            </span>
            <span className="text-xs text-grayscale-1100 whitespace-nowrap">
              <b className="text-grayscale-1300">{c.currency}</b> · {c.region_name}
            </span>
          </button>
        ))}
        {!shown.length && (
          <div className="px-3 py-4 text-sm text-grayscale-1100 text-center">
            Nothing matches “{q}”.
          </div>
        )}
      </div>
    </Modal>
  );
};

/** Currencies a plan may be priced in. Mirrors fx_rates.SUPPORTED — kept short
 *  on purpose, because each one is a Razorpay Plan somebody has to keep alive
 *  in both test and live mode. */
const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AUD', 'CAD', 'SGD', 'AED', 'JPY', 'CHF'];

/**
 * What one plan costs in each region.
 *
 * The backend has taken region prices as authoritative since it shipped —
 * billing_service reads billing_plan_prices before falling back to the legacy
 * single-price columns — but there was no screen to put a row in, so every
 * customer everywhere was still being charged the fallback. This is that
 * screen: it is where Voyager becomes €89 and CHF 79.
 *
 * The conversion beside each amount is advisory and says so. It answers the
 * question the form actually poses — these amounts are meant to be the same
 * product, are they still roughly the same money? — so a price set once and
 * never revisited shows up as a drift number rather than as a customer
 * noticing first.
 */
const RegionPrices: React.FC<{
  userId: string; planCode: string; legacyInrPaise: number;
  flash: (m: string) => void; fail: (m: string) => void;
}> = ({ userId, planCode, legacyInrPaise, flash, fail }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState<any | null>(null);
  const [draft, setDraft] = useState<Record<string, { currency: string; amount: string }>>({});

  const load = async () => {
    setBusy(true);
    const r = await apiService.adminBillingPlanPrices(userId, planCode);
    setBusy(false);
    if (!(r.success && r.data?.success)) { fail(r.error || 'Could not load region prices'); return; }
    setData(r.data);
    const d: Record<string, { currency: string; amount: string }> = {};
    (r.data.regions || []).forEach((reg: any) => {
      const row = (r.data.prices || []).find((x: any) => x.region_code === reg.code);
      d[reg.code] = {
        currency: row?.currency || reg.currency || 'USD',
        amount: row ? String(row.amount_major) : '',
      };
    });
    setDraft(d);
  };

  const toggle = () => { const next = !open; setOpen(next); if (next && !data) load(); };

  const save = async (regionCode: string) => {
    const d = draft[regionCode];
    const amount = parseFloat(d?.amount ?? '');
    if (isNaN(amount) || amount < 0) { fail('Enter a price, e.g. 89'); return; }
    setBusy(true);
    const r = await apiService.adminBillingSetPlanPrice(
      userId, planCode, regionCode, d.currency, amount);
    setBusy(false);
    if (r.success && r.data?.success) {
      flash(`${planCode} · ${regionCode}: ${d.currency} ${amount}`);
      load();
    } else fail(r.error || 'Could not save the price');
  };

  const remove = async (regionCode: string) => {
    if (!window.confirm(`Remove the ${regionCode} price for ${planCode}?\n\n`
      + 'That region falls back to the legacy single price until you set it again.')) return;
    setBusy(true);
    const r = await apiService.adminBillingDeletePlanPrice(userId, planCode, regionCode);
    setBusy(false);
    if (r.success && r.data?.success) { flash(`${planCode} · ${regionCode} removed`); load(); }
    else fail(r.error || 'Could not remove the price');
  };

  const drift = (cur: string) =>
    (data?.comparison || []).find((c: any) => c.currency === cur);
  const fx = data?.fx;

  return (
    <div className="md:col-span-6 border-t border-dashed border-grayscale-300 pt-2">
      <button type="button" onClick={toggle}
        className="text-xs text-grayscale-1200 underline underline-offset-2">
        {open ? '▾' : '▸'} Prices by region &amp; currency
        {data ? ` — ${(data.prices || []).length} set` : ''}
      </button>

      {open && (
        <div className="mt-2 flex flex-col gap-2">
          <div className="text-[11px] text-grayscale-1100 max-w-3xl">
            A region price overrides the single price above for anyone in that
            region. Set in whole units — 89, not 8900. Changing an amount clears
            that row&apos;s cached gateway plan, so the next subscriber is billed
            the new figure; people already subscribed keep what they signed up at.
          </div>

          {busy && !data && <div className="text-xs text-grayscale-1100">Loading…</div>}

          {data && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-grayscale-1100">
                    <tr>
                      {['Region', 'Countries', 'Currency', 'Price / yr', 'vs INR', ''].map(h => (
                        <th key={h} className="text-left font-medium py-1 pr-3">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(data.regions || []).map((reg: any) => {
                      const row = (data.prices || []).find((x: any) => x.region_code === reg.code);
                      const d = draft[reg.code] || { currency: reg.currency, amount: '' };
                      const cmp = row ? drift(row.currency) : null;
                      return (
                        <tr key={reg.code} className="border-t border-grayscale-200">
                          <td className="py-1.5 pr-3">
                            <div className="font-medium text-grayscale-1300">{reg.name}</div>
                            <div className="text-grayscale-1100 font-mono text-[10px]">{reg.code}</div>
                          </td>
                          <td className="py-1.5 pr-3 max-w-[14rem] truncate text-grayscale-1100"
                              title={(reg.countries || []).join(', ')}>
                            {reg.code === 'default'
                              ? 'everyone not matched above'
                              : (reg.countries || []).slice(0, 4).join(', ')
                                + ((reg.countries || []).length > 4
                                   ? ` +${reg.countries.length - 4}` : '')}
                          </td>
                          <td className="py-1.5 pr-3">
                            <select value={d.currency}
                              onChange={e => setDraft(x => ({ ...x,
                                [reg.code]: { ...d, currency: e.target.value } }))}
                              className="h-8 px-1.5 rounded-md border-[0.5px] border-primary-300 text-xs">
                              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </td>
                          <td className="py-1.5 pr-3">
                            <input value={d.amount} placeholder="—" inputMode="decimal"
                              onChange={e => setDraft(x => ({ ...x,
                                [reg.code]: { ...d, amount: e.target.value } }))}
                              className="h-8 px-2 w-24 rounded-md border-[0.5px] border-primary-300 text-xs" />
                          </td>
                          <td className="py-1.5 pr-3 whitespace-nowrap">
                            {cmp && cmp.expected_major != null ? (
                              <span title={`Converting the INR price at today's rate gives about `
                                + `${cmp.currency} ${cmp.expected_major}`}
                                className={Math.abs(cmp.drift_pct ?? 0) > 15
                                  ? 'text-amber-700' : 'text-grayscale-1100'}>
                                {(cmp.drift_pct ?? 0) > 0 ? '+' : ''}{cmp.drift_pct}%
                                <span className="text-grayscale-1100">
                                  {' '}(≈ {cmp.expected_major})
                                </span>
                              </span>
                            ) : <span className="text-grayscale-1100">—</span>}
                          </td>
                          <td className="py-1.5 text-right whitespace-nowrap">
                            <Btn subtle disabled={busy} onClick={() => save(reg.code)}>Save</Btn>{' '}
                            {row && (
                              <Btn subtle disabled={busy}
                                onClick={() => remove(reg.code)}>Remove</Btn>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-[11px] text-grayscale-1100 max-w-2xl">
                  {fx?.available
                    ? <>Rates from {fx.source}, {fx.as_of}
                        {fx.stale && <span className="text-amber-700"> · older than 6 hours</span>}.
                        {' '}Indicative only: the customer is charged the amount set here in its own
                        currency, and settlement converts at the bank&apos;s rate on the payment day.</>
                    : <span className="text-amber-700">
                        Conversion unavailable{fx?.error ? ` (${fx.error})` : ''} — prices still save,
                        there is just nothing to compare them against.
                      </span>}
                </div>
                {!((data.prices || []).length) && legacyInrPaise > 0 && (
                  <Btn subtle disabled={busy} onClick={async () => {
                    setBusy(true);
                    const r = await apiService.adminBillingSeedRegionPrices(userId);
                    setBusy(false);
                    if (r.success && r.data?.success) {
                      flash('Legacy prices copied into the region table');
                      load();
                    } else fail(r.error || 'Seeding failed');
                  }}>Start from the current prices</Btn>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Which countries bill in which currency.
 *
 * Editing this is rarer than editing a price, but a price is meaningless
 * without it: a EUR row does nothing until some country resolves to the region
 * that holds it.
 */
const RegionMap: React.FC<{
  userId: string; flash: (m: string) => void; fail: (m: string) => void;
}> = ({ userId, flash, fail }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    const r = await apiService.adminBillingRegions(userId);
    setBusy(false);
    if (r.success && r.data?.success) {
      setRows((r.data.regions || []).map((x: any) => ({ ...x, _c: (x.countries || []).join(', ') })));
    } else fail(r.error || 'Could not load regions');
  };
  const toggle = () => { const next = !open; setOpen(next); if (next && !rows) load(); };

  const save = async () => {
    if (!rows) return;
    setBusy(true);
    const payload = rows.map(r => ({
      code: r.code, name: r.name, currency: r.currency,
      countries: String(r._c || '').split(',').map((s: string) => s.trim()).filter(Boolean),
    }));
    const r = await apiService.adminBillingSetRegions(userId, payload);
    setBusy(false);
    if (r.success && r.data?.success) { flash('Regions saved'); setRows(null); load(); }
    else fail(r.error || 'Could not save the regions');
  };

  return (
    <Card>
      <button type="button" onClick={toggle}
        className="text-sm font-bold text-grayscale-1300 font-grotesk">
        {open ? '▾' : '▸'} Regions — which countries bill in which currency
      </button>
      {open && (
        <div className="mt-3 flex flex-col gap-2">
          <div className="text-xs text-grayscale-1100 max-w-3xl">
            Countries are matched case-insensitively and accept aliases, so
            <span className="font-mono"> GERMANY, DE, DEU</span> all land in the same
            place. Anything unmatched falls to <span className="font-mono">default</span>,
            which is most accounts — most have no country recorded at all.
          </div>
          {busy && !rows && <div className="text-xs text-grayscale-1100">Loading…</div>}
          {rows && (
            <>
              {rows.map((r, i) => (
                <div key={r.code} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center
                  border-t border-grayscale-200 pt-2">
                  <div className="md:col-span-2 font-mono text-xs text-grayscale-1200">{r.code}</div>
                  <input className="md:col-span-3 h-9 px-2 rounded-md border-[0.5px] border-primary-300 text-sm"
                    value={r.name} placeholder="Name"
                    onChange={e => setRows(xs => xs!.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                  <select className="md:col-span-2 h-9 px-2 rounded-md border-[0.5px] border-primary-300 text-sm"
                    value={r.currency}
                    onChange={e => setRows(xs => xs!.map((x, j) => j === i ? { ...x, currency: e.target.value } : x))}>
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input className="md:col-span-5 h-9 px-2 rounded-md border-[0.5px] border-primary-300 text-sm"
                    value={r._c} placeholder={r.code === 'default' ? 'everything else' : 'GERMANY, DE, FRANCE, FR'}
                    onChange={e => setRows(xs => xs!.map((x, j) => j === i ? { ...x, _c: e.target.value } : x))} />
                </div>
              ))}
              <div className="flex items-center gap-2 border-t border-grayscale-200 pt-2">
                <Btn subtle disabled={busy} onClick={() => setRows(xs => [...(xs || []), {
                  code: `region_${(xs || []).length + 1}`, name: '', currency: 'EUR', _c: '', countries: [],
                }])}>Add a region</Btn>
                <Btn disabled={busy} onClick={save}>Save regions</Btn>
                <span className="text-[11px] text-grayscale-1100">
                  A new region has no price until you set one per plan below.
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
};

interface AdminBillingPageProps {
  /** Rendered inside the AdminPage shell: the shell already gated access and
   *  painted the chrome, so skip this page's own gate, header and wrapper. */
  embedded?: boolean;
  /** The signed-in admin's email, when embedded (the shell knows it). */
  adminEmail?: string;
  /** The sub-tab an alert asked for (the shell hands it over once, then
   *  clears it, so the admin can move freely afterwards). */
  initialTab?: string;
}

const AdminBillingPage: React.FC<AdminBillingPageProps> = ({
  embedded = false, adminEmail: adminEmailProp = '', initialTab,
}) => {
  // Re-read per call: a sign-in on this page changes it mid-session.
  const userId = apiService.getStoredUserId() || '';
  const [gate, setGate] = useState<Gate>(embedded ? 'ok' : 'checking');
  const [adminEmail, setAdminEmail] = useState(adminEmailProp);
  const [tab, setTab] = useState<Tab>(isTab(initialTab) ? initialTab : 'users');
  useEffect(() => { if (isTab(initialTab)) setTab(initialTab); }, [initialTab]);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  // users
  const [search, setSearch] = useState('');
  // Lifecycle + custom-tag filter on the Users list (server-side, so the
  // paging and the total stay right); `tagParams` is what goes on the URL.
  const [tagF, setTagF] = useState<TagFilterValue>(EMPTY_TAG_FILTER);
  const [tagParams, setTagParams] = useState<Record<string, string>>({});
  const [users, setUsers] = useState<any[]>([]);
  // Countries an admin may set, each with the currency it bills in. Loaded
  // once: it is a config read, not per-user.
  const [countries, setCountries] = useState<any[]>([]);
  /** Which account's billing country is being chosen, if any. */
  const [countryFor, setCountryFor] = useState<any | null>(null);
  /** Which account's Change plan dialog is open, if any. */
  const [changePlanFor, setChangePlanFor] = useState<any | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [page, setPage] = useState(0);
  const [pageInfo, setPageInfo] = useState({ total: 0, pages: 1, per_page: PER_PAGE });
  const [globalPayOnce, setGlobalPayOnce] = useState(false);
  // plans
  const [plans, setPlans] = useState<any[]>([]);
  const [usdCap, setUsdCap] = useState<any>(null);
  const [capBusy, setCapBusy] = useState(false);
  // coupons
  const [coupons, setCoupons] = useState<any[]>([]);
  const [couponForm, setCouponForm] = useState({ code: '', description: '', discount_type: 'percent', discount_value: 10, max_redemptions: '', valid_until: '' });
  // payments
  const [payments, setPayments] = useState<any[]>([]);
  // trials — the whole /admin/billing/trials answer ({funnel, cancellations,
  // events, trials}); null until the tab has loaded once.
  const [trials, setTrials] = useState<any | null>(null);
  const [trialsBusy, setTrialsBusy] = useState(false);
  // Which lifecycle segment the Trials table shows. Server-side (the
  // `segment` param) rather than a client filter, so the lapsed list is the
  // same set the sweep and the follow-up mail see.
  const [trialSegment, setTrialSegment] = useState<TrialSegment>('all');
  // Trial lifecycle sweep — the whole GET billing/trial_lifecycle_status
  // answer ({scheduler_running, last_run, settings, …counts}); `lifecycleForm`
  // is the settings being edited, saved as one POST.
  const [lifecycle, setLifecycle] = useState<any | null>(null);
  const [lifecycleErr, setLifecycleErr] = useState('');
  const [lifecycleForm, setLifecycleForm] = useState<any | null>(null);
  const [lifecycleBusy, setLifecycleBusy] = useState<'' | 'save' | 'run'>('');
  // Win-back offer — the coupon a cancel hands back. `winback` is the whole
  // GET users/winback_settings answer ({settings, promotion, candidates});
  // `winbackForm` is the settings being edited, saved as one POST so a
  // half-changed form never goes live.
  const [winback, setWinback] = useState<any | null>(null);
  const [winbackForm, setWinbackForm] = useState<any | null>(null);
  const [winbackBusy, setWinbackBusy] = useState(false);
  // promotions + trial
  const [promos, setPromos] = useState<any[]>([]);
  const [trialCfg, setTrialCfg] = useState<any>({ enabled: false, days: 7, plan_code: 'voyager' });
  const [promoForm, setPromoForm] = useState<any>(null);
  // Offers live in Razorpay, not here — they cannot be created over the API,
  // so the form picks from whatever the account actually has.
  const [rzOffers, setRzOffers] = useState<any[]>([]);
  // One-time codes for a promotion: which promo's codes are open, the codes
  // themselves, and the mint controls.
  const [voucherFor, setVoucherFor] = useState<any>(null);
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [vCount, setVCount] = useState('10');
  const [vPrefix, setVPrefix] = useState('');
  const [rzOffersError, setRzOffersError] = useState('');
  // Free-tier paywall switch
  const [lockCfg, setLockCfg] = useState<any>(null);
  // sign-in form (shown instead of bouncing the admin to the landing page)
  const [login, setLogin] = useState({ email: '', password: '', otp: '', mode: 'password' as 'password' | 'otp', sent: false, busy: false });
  // manual grant modal
  const [grant, setGrant] = useState<{ email: string; plan_code: string; months: number; note: string } | null>(null);
  const [refund, setRefund] = useState<{
    payment_id: string; email: string; amount_minor: number; max_minor: number;
    currency: string; reason: string; full: boolean; end_access: boolean;
  } | null>(null);
  // Orphaned billing — rows whose user id has no users row any more (the
  // account was deleted and re-created under a new id while its mandate
  // stayed live at Razorpay). null until the Payments tab has read once;
  // `orphanTarget` holds the typed target id per ghost when the server had
  // no e-mail match to suggest; `orphanBusy` is the ghost being moved.
  const [orphans, setOrphans] = useState<any[] | null>(null);
  // Set when the orphans GET failed, so the card can say so instead of
  // "Loading…" for ever (the error banner clears on the next flash, the
  // card does not).
  const [orphansErr, setOrphansErr] = useState('');
  const [orphanTarget, setOrphanTarget] = useState<Record<string, string>>({});
  const [orphanBusy, setOrphanBusy] = useState('');
  // Refund from the Users tab: a person rather than a payment id. The dialog
  // loads what they have paid that can still come back, then the admin picks
  // one and says why. `reason` gates the button on purpose.
  const [userRefund, setUserRefund] = useState<{
    user: any; loading: boolean; busy: boolean; payments: any[]; subscription: any | null;
    payment_id: string; full: boolean; amount_minor: number; end_access: boolean; reason: string;
  } | null>(null);

  const flash = (m: string) => { setMsg(m); setErr(''); setTimeout(() => setMsg(''), 4000); };
  const fail = (m: string) => { setErr(m); setMsg(''); };

  const checkAccess = async () => {
    const uid = apiService.getStoredUserId() || '';
    if (!uid) { setGate('signed_out'); return; }
    const res = await apiService.adminBillingCheck(uid);
    if (!res.success || !res.data) { setGate('signed_out'); return; }
    if (!res.data.signed_in) setGate('signed_out');
    else if (!res.data.is_admin) setGate('denied');
    else { setGate('ok'); setAdminEmail(res.data.email); }
  };

  useEffect(() => {
    if (!embedded) checkAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const storeSession = (d: any) => {
    apiService.setStoredAccessToken(d.access_token);
    apiService.setStoredRefreshToken(d.refresh_token);
    apiService.setStoredUserId(d.user_id);
  };

  const doPasswordLogin = async () => {
    setLogin(l => ({ ...l, busy: true })); setErr('');
    const r = await apiService.loginWithPassword(login.email.trim(), login.password);
    setLogin(l => ({ ...l, busy: false }));
    if (r.success && r.data?.access_token) { storeSession(r.data); checkAccess(); }
    else fail(r.error || 'Sign in failed — check your email and password.');
  };

  const doSendOtp = async () => {
    setLogin(l => ({ ...l, busy: true })); setErr('');
    const r = await apiService.sendOTP(login.email.trim(), 'login');
    setLogin(l => ({ ...l, busy: false, sent: r.success }));
    if (r.success) flash('Code sent — check your email.');
    else fail(r.error || 'Could not send the code');
  };

  const doVerifyOtp = async () => {
    setLogin(l => ({ ...l, busy: true })); setErr('');
    const r = await apiService.verifyOTP(login.email.trim(), login.otp.trim(), 'login');
    setLogin(l => ({ ...l, busy: false }));
    if (r.success && r.data?.access_token) { storeSession(r.data); checkAccess(); }
    else fail(r.error || 'That code did not work');
  };

  const loadUsers = async (q = search, p = page, tp = tagParams) => {
    const r = await fetchBillingOverview(userId, q, p, PER_PAGE, tp);
    if (r.success && r.data?.success) {
      setUsers(r.data.users);
      setCounts(r.data.subscription_counts || {});
      setPageInfo({ total: r.data.total, pages: r.data.pages, per_page: r.data.per_page });
      setGlobalPayOnce(!!r.data.allow_one_time_all);
      setPage(r.data.page);
    } else fail(r.error || 'Failed to load users');
  };
  const loadCountries = async () => {
    if (countries.length) return countries;
    const r = await apiService.adminBillingCountries(userId);
    const list = (r.success && r.data?.success) ? (r.data.countries || []) : [];
    setCountries(list);
    return list;
  };

  const loadPlans = async () => {
    const r = await apiService.adminBillingPlans(userId);
    if (r.success && r.data?.success) setPlans(r.data.plans.map((p: any) => ({ ...p, _price_inr: String(p.price_inr_paise / 100) })));
    else fail(r.error || 'Failed to load plans');
    const c = await apiService.adminBillingCapabilities(userId);
    if (c.success && c.data?.success) setUsdCap(c.data.usd_subscriptions);
  };

  const recheckCapability = async () => {
    setCapBusy(true);
    const c = await apiService.adminBillingCapabilities(userId, true);
    setCapBusy(false);
    if (c.success && c.data?.success) {
      setUsdCap(c.data.usd_subscriptions);
      if (c.data.usd_subscriptions?.available) flash('International subscriptions are live — overseas customers can now auto-renew.');
      else fail(`Still blocked: ${c.data.usd_subscriptions?.error || 'unknown'}`);
    } else fail(c.error || 'Check failed');
  };
  const loadCoupons = async () => {
    const r = await apiService.adminBillingCoupons(userId);
    if (r.success && r.data?.success) setCoupons(r.data.coupons);
    else fail(r.error || 'Failed to load coupons');
  };
  const loadLock = async () => {
    const r = await apiService.adminOnboardingSettings(userId);
    if (r.success && r.data?.success) setLockCfg(r.data.lock);
  };

  const saveLock = async (patch: Record<string, any>) => {
    const next = { ...(lockCfg || {}), ...patch };
    const r = await apiService.adminSetFreeTierLock(userId, next);
    if (r.success && r.data?.success) { setLockCfg(r.data.lock); flash('Free-tier lock updated'); }
    else fail(r.error || 'Failed');
  };

  const loadPromos = async () => {
    const r = await apiService.adminBillingPromotions(userId);
    if (r.success && r.data?.success) { setPromos(r.data.promotions); setTrialCfg(r.data.trial); }
    else fail(r.error || 'Failed to load promotions');
    if (!plans.length) loadPlans();
    // Best-effort: a Razorpay outage must not stop the promotions tab loading.
    const o = await apiService.adminBillingRazorpayOffers(userId);
    if (o.success && o.data?.success) {
      setRzOffers(o.data.offers || []);
      setRzOffersError(o.data.error || '');
    } else {
      setRzOffersError(o.error || 'could not reach Razorpay');
    }
  };

  const loadPayments = async () => {
    const r = await apiService.adminBillingPayments(userId);
    if (r.success && r.data?.success) setPayments(r.data.payments);
    else fail(r.error || 'Failed to load payments');
  };

  const loadOrphans = async () => {
    const r = await apiService.adminBillingOrphans(userId);
    if (r.success && r.data?.success) { setOrphans(r.data.rows || []); setOrphansErr(''); }
    else {
      // A frontend deployed ahead of the backend (they ship separately) or
      // a 500 must not leave the card stuck on "Loading…" above a payments
      // table that loaded fine.
      const m = r.error || 'Failed to load orphaned payments';
      setOrphansErr(m);
      setOrphans(o => o || []);
      fail(m);
    }
  };

  /** Move a ghost id's billing rows onto a real account. Everything that
   *  shows those rows is re-read afterwards: the payment now names a user,
   *  the orphan row is gone, and the target's plan on the Users tab may
   *  have changed (the moved subscription is theirs now). */
  const reattribute = async (ghostId: string, targetId: string, label: string) => {
    setOrphanBusy(ghostId);
    const r = await apiService.adminBillingReattribute(userId, ghostId, targetId.trim());
    setOrphanBusy('');
    if (r.success && r.data?.success) {
      // The answer is per-table counts; naming them here would tie the
      // panel to the server's exact keys, so every numeric field is shown.
      const counts = Object.entries(r.data)
        .filter(([k, v]) => typeof v === 'number' && k !== 'status')
        .map(([k, v]) => `${v} ${k.replace(/_/g, ' ')}`)
        .join(', ');
      flash(`Re-attributed to ${label}${counts ? ` — ${counts}` : ''}`);
      loadPayments(); loadOrphans(); loadUsers();
    } else fail(r.error || 'Re-attribution failed');
  };

  /** What is still refundable on a payment: a partial refund earlier leaves
   *  the remainder, and that is the most a "full" refund can now be. */
  const refundableMinor = (p: any) =>
    Math.max(0, Number(p.amount_paise || 0) - Number(p.refunded_so_far || 0));

  /** Open the refund dialog for one account and fetch what can be refunded.
   *  The newest payment is pre-selected: it is the one a "charged after
   *  cancelling" complaint is about nearly every time. */
  const openUserRefund = async (u: any) => {
    setUserRefund({
      user: u, loading: true, busy: false, payments: [], subscription: null,
      payment_id: '', full: true, amount_minor: 0, end_access: true, reason: '',
    });
    const r = await apiService.adminBillingUserPayments(userId, u.user_id);
    // The dialog may have been closed, or reopened for SOMEONE ELSE, while
    // this was in flight: a slow answer for user A must never land in user
    // B's dialog with A's payment pre-selected on the Refund button.
    const stillOurs = (s: typeof userRefund) => !!s && s.user?.user_id === u.user_id;
    if (!(r.success && r.data?.success)) {
      setUserRefund(s => (stillOurs(s) ? null : s));
      fail(r.error || 'Could not load their payments');
      return;
    }
    const pays: any[] = r.data.payments || [];
    const first = pays[0];
    setUserRefund(s => (stillOurs(s) && s ? {
      ...s, loading: false, payments: pays, subscription: r.data.subscription || null,
      payment_id: first?.razorpay_payment_id || '',
      amount_minor: first ? refundableMinor(first) : 0,
    } : s));
  };


  /** `keepDraft` is for the Enabled toggle: it saves only that flag, so a
   *  code or message the admin has typed but not yet saved must survive the
   *  re-read — only `enabled` is taken from the server then. Everything
   *  else (first load, a full Save) replaces the form with what is stored. */
  const loadWinback = async (keepDraft = false) => {
    const { data, error } = await panelGet('users', 'winback_settings');
    if (data) {
      setWinback(data);
      setWinbackForm((f: any) => (keepDraft && f
        ? { ...f, enabled: !!data.settings?.enabled }
        : { ...(data.settings || {}) }));
    }
    else fail(error || 'Failed to load the win-back offer');
  };

  /** One POST for whatever changed. The toggle sends only `enabled` so a
   *  quick on/off never carries an unsaved code change with it; Save sends
   *  the form. The server validates (the code must be a live coded
   *  promotion, or blank) and answers with what is now stored, so the form
   *  is re-read from that rather than trusted from here. */
  const saveWinback = async (patch: Record<string, any>) => {
    setWinbackBusy(true);
    // Judged on `error`, not on the body: a handler that answers with an
    // empty object on success would otherwise read as a failure.
    const { error } = await panelPost('users', 'winback_settings_set', patch);
    setWinbackBusy(false);
    if (error) { fail(error); return; }
    const toggleOnly = patch.enabled !== undefined && Object.keys(patch).length === 1;
    // The set handler may answer with the stored settings alone or with the
    // full GET shape; either way a re-read keeps the promotion summary and
    // the form in step with what was just saved.
    await loadWinback(toggleOnly);
    flash(toggleOnly
      ? `Win-back offer ${patch.enabled ? 'enabled' : 'disabled'}`
      : 'Win-back offer saved');
  };

  const loadTrials = async (segment: TrialSegment = trialSegment) => {
    setTrialsBusy(true);
    // The offer settings sit on this tab beside the funnel they feed, so
    // they load with it; a failure there must not blank the tables.
    void loadWinback();
    const r = await apiService.adminBillingTrials(userId, 200, segment);
    setTrialsBusy(false);
    if (r.success && r.data?.success) setTrials(r.data);
    else fail(r.error || 'Failed to load trials');
  };

  /** The sweep's status card (Promotions & trials tab). Its own error, not
   *  the page banner: the trial settings beside it must still load when
   *  the backend has not shipped the lifecycle module yet. */
  const loadLifecycle = async (keepDraft = false) => {
    const { data, error } = await panelGet('billing', 'trial_lifecycle_status');
    if (data) {
      setLifecycle(data); setLifecycleErr('');
      setLifecycleForm((f: any) => (keepDraft && f ? f : { ...(data.settings || {}) }));
    } else {
      setLifecycleErr(error || 'Failed to load the trial lifecycle status');
    }
  };

  /** One POST for the settings form. The server validates the patch and
   *  answers with what is now stored; a re-read keeps the card in step. */
  const saveLifecycle = async (patch: Record<string, any>) => {
    setLifecycleBusy('save');
    const { error } = await panelPost('billing', 'trial_lifecycle_settings_set', patch);
    setLifecycleBusy('');
    if (error) { fail(error); return; }
    await loadLifecycle();
    flash('Trial lifecycle settings saved');
  };

  /** Run the sweep now, as this admin. Lapses due trials and sends any due
   *  ending-soon / lapsed mails — each stamped before sending, so a second
   *  click cannot double-send. */
  const runLifecycleNow = async () => {
    setLifecycleBusy('run');
    const { data, error } = await panelPost('billing', 'trial_lifecycle_run_now');
    setLifecycleBusy('');
    if (error) { fail(error); return; }
    // The answer is the counts ({lapsed, ending_sent, lapsed_sent, errors});
    // every numeric field is shown so the panel is not tied to exact keys.
    const counts = Object.entries(data || {})
      .filter(([, v]) => typeof v === 'number')
      .map(([k, v]) => `${v} ${k.replace(/_/g, ' ')}`)
      .join(', ');
    flash(`Sweep ran${counts ? ` — ${counts}` : ''}`);
    loadLifecycle();
    if (trials) loadTrials();
  };

  useEffect(() => {
    if (gate !== 'ok') return;
    if (tab === 'users') loadUsers();
    if (tab === 'plans') loadPlans();
    if (tab === 'promos') { loadPromos(); loadLock(); loadLifecycle(); }
    if (tab === 'coupons') loadCoupons();
    if (tab === 'trials') loadTrials();
    if (tab === 'payments') { loadPayments(); loadOrphans(); }
    if (tab === 'gateway') loadGateway();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate, tab]);

  if (gate === 'checking') {
    return <div className="min-h-screen bg-grayscale-100 flex items-center justify-center text-sm text-grayscale-1100">Checking access…</div>;
  }
  if (gate === 'signed_out') {
    return (
      <div className="min-h-screen bg-grayscale-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-custom-light p-8 max-w-sm w-full flex flex-col gap-3">
          <h1 className="text-xl font-bold text-grayscale-1300 font-grotesk">Admin sign in</h1>
          <p className="text-sm text-grayscale-1200 -mt-1">
            Use your normal Cosmos account. Admin access is granted per email.
          </p>
          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
          {msg && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{msg}</div>}

          <input type="email" autoFocus placeholder="you@universitytech.io" value={login.email}
            onChange={e => setLogin(l => ({ ...l, email: e.target.value }))}
            className="h-11 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />

          {login.mode === 'password' ? (
            <>
              <input type="password" placeholder="Password" value={login.password}
                onChange={e => setLogin(l => ({ ...l, password: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && doPasswordLogin()}
                className="h-11 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
              <button onClick={doPasswordLogin} disabled={login.busy || !login.email || !login.password}
                className="h-11 rounded-full font-grotesk text-sm font-medium bg-primary-500 text-grayscale-100 disabled:bg-primary-100 disabled:text-primary-300">
                {login.busy ? 'Signing in…' : 'Sign in'}
              </button>
              <button onClick={() => setLogin(l => ({ ...l, mode: 'otp' }))}
                className="text-xs text-primary-700 underline self-center">Email me a code instead</button>
            </>
          ) : (
            <>
              {login.sent && (
                <input placeholder="6-digit code" value={login.otp}
                  onChange={e => setLogin(l => ({ ...l, otp: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && doVerifyOtp()}
                  className="h-11 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm tracking-widest" />
              )}
              <button onClick={login.sent ? doVerifyOtp : doSendOtp}
                disabled={login.busy || !login.email || (login.sent && !login.otp)}
                className="h-11 rounded-full font-grotesk text-sm font-medium bg-primary-500 text-grayscale-100 disabled:bg-primary-100 disabled:text-primary-300">
                {login.busy ? 'Working…' : login.sent ? 'Verify code' : 'Send code'}
              </button>
              <button onClick={() => setLogin(l => ({ ...l, mode: 'password', sent: false }))}
                className="text-xs text-primary-700 underline self-center">Use password instead</button>
            </>
          )}
          <a href="/" className="text-xs text-grayscale-1100 underline self-center mt-1">Back to Cosmos</a>
        </div>
      </div>
    );
  }
  if (gate === 'denied') {
    return (
      <div className="min-h-screen bg-grayscale-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-custom-light p-8 max-w-md text-center">
          <h1 className="text-xl font-bold text-grayscale-1300 font-grotesk mb-2">Access denied</h1>
          <p className="text-sm text-grayscale-1200 mb-4">
            <span className="font-medium">{adminEmail || 'This account'}</span> is not an admin.
            Ask a super admin to add you.
          </p>
          <button
            onClick={() => { apiService.clearStoredAuth(); setGate('signed_out'); }}
            className="py-2.5 px-5 rounded-full font-grotesk text-sm font-medium bg-primary-500 text-grayscale-100">
            Sign in with another account
          </button>
        </div>
      </div>
    );
  }

  const UserHead: React.FC = () => (
    <thead><tr className="text-left text-xs text-grayscale-1100 border-b border-grayscale-300">
      <th className="py-2 pr-3">Email</th><th className="pr-3">Name</th>
      <th className="pr-3">Lifecycle · tags</th><th className="pr-3">Plan</th>
      <th className="pr-3">Status</th><th className="pr-3">Source</th><th className="pr-3">Ends</th>
      <th className="pr-3">Pay-once</th><th></th>
    </tr></thead>
  );

  const UserRow: React.FC<{ u: any }> = ({ u }) => (
    <tr className="border-b border-grayscale-200">
      <td className="py-2 pr-3 text-grayscale-1300">
        {u.email}
        {u.is_admin && (
          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700">admin</span>
        )}
      </td>
      <td className="pr-3 text-grayscale-1200">{u.name || '—'}</td>
      <td className="pr-3"><span className="inline-block min-w-[12rem]"><UserTagCells row={withLifecycle(u)} /></span></td>
      <td className="pr-3">{u.plan_code || '—'}</td>
      <td className="pr-3 whitespace-nowrap">
        {u.sub_status
          ? <span className={u.sub_status === 'active' ? 'text-green-700' : 'text-grayscale-1100'}>
              {u.sub_status}{u.auto_renew ? ' · auto' : ''}</span>
          : '—'}
      </td>
      <td className="pr-3 text-grayscale-1100">{u.source || '—'}</td>
      <td className="pr-3">{fmtDate(u.current_end)}</td>
      <td className="pr-3">
        <button
          onClick={async () => {
            const r = await apiService.adminBillingSetUserFlag(userId, u.user_id, !u.allow_one_time);
            if (r.success && r.data?.success) { flash(`Pay-once ${!u.allow_one_time ? 'enabled' : 'disabled'} for ${u.email}`); loadUsers(); }
            else fail(r.error || 'Failed');
          }}
          disabled={globalPayOnce}
          title={globalPayOnce ? 'Enabled for everyone by the global switch' : ''}
          className={`text-xs px-2 py-0.5 rounded-full border disabled:opacity-50 ${
            (u.allow_one_time || globalPayOnce)
              ? 'border-green-600 text-green-700 bg-green-50'
              : 'border-grayscale-400 text-grayscale-1100'}`}>
          {globalPayOnce ? 'all' : u.allow_one_time ? 'enabled' : 'off'}
        </button>
      </td>
      <td className="text-right whitespace-nowrap">
        <Btn subtle onClick={() => setGrant({ email: u.email, plan_code: 'voyager', months: 12, note: '' })}>Grant</Btn>{' '}
        {u.sub_status && (
          <Btn subtle onClick={async () => {
            const raw = window.prompt(`Extend ${u.email}'s ${u.source === 'trial' ? 'trial' : 'pack'} by how many days?`, '7');
            if (!raw) return;
            const r = await apiService.adminBillingExtend(userId, u.user_id, Number(raw));
            if (r.success && r.data?.success) { flash(`Extended to ${fmtDate(r.data.current_end)}`); loadUsers(); }
            else fail(r.error || 'Extend failed');
          }}>Extend</Btn>
        )}{' '}
        {u.sub_status === 'active' && (
          <Btn subtle onClick={async () => {
            // A prompt rather than a confirm: dismissing it still aborts,
            // and OK with the box empty still cancels — but the row now
            // carries why WE ended it, next to the reasons customers give.
            const reason = window.prompt(
              `Cancel ${u.email}'s subscription?\n\n`
              + 'Optional: why (recorded on the cancellation).', '');
            if (reason === null) return;
            const r = await apiService.adminBillingCancelSub(userId, u.user_id, reason.trim());
            if (r.success && r.data?.success) { flash('Cancelled'); loadUsers(); }
            else fail(r.error || 'Failed');
          }}>Cancel</Btn>
        )}{' '}
        {u.sub_status === 'active' && (
          <Btn subtle title="Give money back on one of this account's payments"
            onClick={() => openUserRefund(u)}>Refund</Btn>
        )}{' '}
        <Btn subtle title="Give this account a free trial, whatever the global switch says"
          onClick={async () => {
            const raw = window.prompt(
              `Grant ${u.email} a free trial of how many days?\n\n`
              + 'Supersedes any current plan. Leave as-is for the configured length.',
              '3');
            if (raw === null) return;
            const days = Number(raw.trim() || 0);
            if (!Number.isFinite(days) || days < 0 || days > 365) { fail('1 to 365 days'); return; }
            const r = await apiService.adminBillingGrantTrial(userId, u.user_id, days);
            if (r.success && r.data?.success) {
              flash(`${u.email}: ${r.data.days}-day trial until ${fmtDate(r.data.current_end)}`);
              loadUsers();
            } else fail(r.error || 'Could not grant the trial');
          }}>Trial</Btn>{' '}
        <Btn subtle title={u.country ? `Billing country: ${u.country}` : 'No billing country set'}
          onClick={async () => {
            // Billing reads users.country first and only falls back to the edge
            // header and IP when it is blank, so setting it IS the override —
            // and it is the only way to exercise the USD path from India.
            // A searchable list rather than a typed value: a typo silently
            // falls through to the default region instead of erroring, and the
            // currency has to be visible at the point of choosing because the
            // currency is the actual consequence.
            await loadCountries();
            setCountryFor(u);
          }}>Country{u.country ? ` · ${u.country}` : ''}</Btn>{' '}
        {/* Every row, not only active ones: the dialog (shared with the user
            drawer) reads the account's plan itself and offers the moves that
            make sense — to Explorer now or at period end, between paid plans,
            or a grant. Refunds stay on the Refund button. */}
        <Btn subtle title="Move this account to another plan (Explorer, Voyager, …), with an optional e-mail"
          onClick={() => setChangePlanFor(u)}>Change plan…</Btn>{' '}
        <Btn subtle onClick={async () => {
          // Two prompts on purpose: this deletes rows rather than flipping a
          // status, and it also cancels the Razorpay subscription so the card
          // stops being charged.
          if (!window.confirm(
            `Delete ALL billing history for ${u.email}?\n\n`
            + 'Their subscriptions and payment records are permanently removed and '
            + 'the account returns to the free tier. Any live Razorpay subscription '
            + 'is cancelled so the card is not charged again.\n\nThis cannot be undone.')) return;
          const r = await apiService.adminClearUserBilling(userId, u.user_id);
          if (r.success && r.data?.success) {
            const g = r.data.gateway || {};
            flash(`Cleared ${r.data.subscriptions_deleted} subscription(s), `
              + `${r.data.payments_deleted} payment(s)`
              + (g.cancelled?.length ? `; cancelled ${g.cancelled.length} at Razorpay` : ''));
            loadUsers();
          } else fail(r.error || 'Clear failed');
        }}>Clear history</Btn>
      </td>
    </tr>
  );

  // ---- Razorpay environment -------------------------------------------
  const [gateway, setGateway] = useState<any>(null);
  const [hooks, setHooks] = useState<any[]>([]);
  const [switching, setSwitching] = useState(false);
  const [resetPhrase, setResetPhrase] = useState('');
  const [newPlan, setNewPlan] = useState({ code: '', name: '', price: '', description: '' });
  const [conciergeCc, setConciergeCc] = useState('');
  const [conciergeInbox, setConciergeInbox] = useState('');

  const loadGateway = async () => {
    const uid = apiService.getStoredUserId() || '';
    const [g, h, c] = await Promise.all([
      apiService.adminRazorpayMode(uid),
      apiService.adminBillingWebhookEvents(uid, 50),
      apiService.adminConciergeRequests(uid, 1),
    ]);
    if (c.success && c.data?.success) {
      setConciergeInbox(c.data.inbox || '');
      setConciergeCc((c.data.cc || []).join(', '));
    }
    if (g.success) setGateway(g.data);
    else setErr(g.error || 'Could not read the Razorpay mode');
    if (h.success) setHooks(h.data?.rows || []);
  };

  const switchMode = async (mode: 'test' | 'live') => {
    // Going live means the next card entered is charged for real, so make the
    // operator say it out loud. The server independently requires confirm.
    if (mode === 'live' && !window.confirm(
      'Switch Razorpay to LIVE?\n\nReal cards will be charged from the next checkout. '
      + 'Make sure the live webhook is configured first.')) return;
    setSwitching(true); setErr('');
    const uid = apiService.getStoredUserId() || '';
    const res = await apiService.adminSetRazorpayMode(uid, mode, mode === 'live');
    setSwitching(false);
    if (res.success) { setGateway(res.data); setMsg(`Razorpay is now in ${mode.toUpperCase()} mode.`); }
    else setErr(res.error || 'Could not switch mode');
  };

  // ---- The Razorpay tab -------------------------------------------------
  // What the gateway holds, next to what we hold. Every other tab reads our
  // tables, and on 2026-09-20 our tables had nothing live for the mandate
  // that charged ₹9,000 — the account behind it had been deleted. Razorpay's
  // dashboard listed it as one of four active subscriptions. This is that
  // list, with the column the dashboard cannot show: our account, or that
  // there is none. `gw` is the whole answer per list ({mode, rows, count,
  // fetched_at, cached, error}); null until read once.
  const [gwSub, setGwSub] = useState<GwKind>('subscriptions');
  const [gwStatus, setGwStatus] = useState('active');
  const [gwQ, setGwQ] = useState('');
  const [gw, setGw] = useState<Record<GwKind, any | null>>({
    subscriptions: null, payments: null, refunds: null });
  const [gwBusy, setGwBusy] = useState(false);
  // The action dialog: stop a mandate, or refund a payment, by gateway id.
  // Both insist on a reason — the cancellation / refund row the server files
  // is the only place "why" survives, and these buttons exist precisely
  // for the rows no account explains.
  const [gwAction, setGwAction] = useState<
    | { kind: 'cancel'; sub: any; reason: string; busy: boolean }
    | { kind: 'refund'; payment_id: string; payer: string; ours: string | null;
        subscription_id: string | null; amount_minor: number; max_minor: number;
        currency: string; full: boolean; end_access: boolean; reason: string; busy: boolean }
    | null>(null);

  /** Read one gateway list. `refresh` bypasses the server's 60 s cache —
   *  the Refresh button, and every re-read after an action. */
  const loadGw = async (kind: GwKind = gwSub, refresh = false) => {
    setGwBusy(true);
    const r = await apiService.adminBillingGateway(userId, kind, {
      count: 100, refresh,
      ...(kind === 'subscriptions' ? { status: gwStatus } : {}),
    });
    setGwBusy(false);
    if (r.success && r.data?.success) setGw(g => ({ ...g, [kind]: r.data }));
    else {
      // The route itself failed (auth, 500, older backend): keep whatever
      // was on screen and say so, rather than blanking a list that loaded.
      const m = r.error || `Could not read Razorpay ${kind}`;
      setGw(g => ({ ...g, [kind]: g[kind] ? { ...g[kind], error: m } : { rows: [], error: m } }));
      fail(m);
    }
  };

  useEffect(() => {
    if (tab === 'razorpay') loadGw(gwSub);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, gwSub, gwStatus]);

  /** Tell the admin shell something alert-worthy changed (a refund or a
   *  stopped mandate is exactly what its banner lists), so it re-reads now
   *  rather than on its next poll. Dispatched on `document`, where AdminPage
   *  listens: an event fired at `window` never reaches a document listener
   *  (events bubble upward, and window is the top), so the banner kept its
   *  stale "live mandates 1" for up to a minute after the cancel. */
  const alertsChanged = () => {
    try { document.dispatchEvent(new Event('utech:alerts-changed')); } catch { /* not in a browser */ }
  };

  /** Open the refund dialog for a gateway payment. `max` is what is still
   *  refundable: Razorpay's amount minus what it has already given back. */
  const openGwRefund = (p: { id: string; amount: number; currency: string;
    amount_refunded?: number; payer: string; ours: string | null; subscription_id?: string | null }) => {
    const max = Math.max(0, Number(p.amount || 0) - Number(p.amount_refunded || 0));
    setGwAction({ kind: 'refund', payment_id: p.id, payer: p.payer, ours: p.ours,
      subscription_id: p.subscription_id || null, amount_minor: max, max_minor: max,
      currency: p.currency || 'INR', full: true, end_access: true, reason: '', busy: false });
  };

  const doGwCancel = async () => {
    if (!gwAction || gwAction.kind !== 'cancel') return;
    const { sub, reason } = gwAction;
    setGwAction(a => a && ({ ...a, busy: true }));
    const r = await apiService.adminBillingGatewayCancel(userId, sub.id, reason.trim());
    if (r.success && r.data?.success) {
      const d = r.data;
      flash(`${sub.id} ${d.gateway === 'already_inactive' ? 'was already stopped' : 'cancelled'} at Razorpay`
        + (d.rows_closed ? ` — ${d.rows_closed} row(s) closed on our side` : ' — no row on our side')
        + (d.local_error ? ` (local update failed: ${d.local_error})` : ''));
      setGwAction(null);
      loadGw('subscriptions', true);
      alertsChanged();
    } else {
      setGwAction(a => a && ({ ...a, busy: false }));
      fail(r.error || 'Cancel failed');
    }
  };

  const doGwRefund = async () => {
    if (!gwAction || gwAction.kind !== 'refund') return;
    const a = gwAction;
    // DangerBtn's arm-then-fire is the confirmation, as on the Users tab's
    // refund; the button label carries the amount.
    setGwAction(x => x && ({ ...x, busy: true }));
    const r = await apiService.adminBillingGatewayRefund(userId, {
      payment_id: a.payment_id, amount_minor: a.full ? null : a.amount_minor,
      reason: a.reason.trim(), end_access: a.end_access,
    });
    if (r.success && r.data?.success) {
      const d = r.data;
      const m = d.mandate || {};
      flash(`Refunded ${d.refund_id || ''}`
        + (d.local ? '' : ' (no row on our side — refunded at the gateway directly)')
        + (d.access_ended ? ', access ended' : '')
        + (m.subscription_id ? ` · mandate ${m.subscription_id}: ${m.gateway || (m.cancelled ? 'cancelled' : 'left running')}` : ''));
      setGwAction(null);
      loadGw(gwSub, true);
      if (gwSub !== 'payments') setGw(g => ({ ...g, payments: null }));
      if (gwSub !== 'refunds') setGw(g => ({ ...g, refunds: null }));
      loadPayments();
      alertsChanged();
    } else {
      setGwAction(x => x && ({ ...x, busy: false }));
      fail(r.error || 'Refund failed');
    }
  };

  /** The "Our account" cell: the one column Razorpay's dashboard cannot
   *  have. Three answers, and two of them are red on purpose — a row we
   *  hold for an account that no longer exists is the incident itself. */
  const ourAccountCell = (our: any, notesUser?: any) => {
    if (our && !our.user_missing && our.email) {
      return (
        <button className="underline text-primary-700 text-left"
          title={`${our.user_id}${our.status ? ` · ${our.status}` : ''}${our.is_trial ? ' · trial' : ''}`}
          onClick={() => { setSearch(our.email); setPage(0); setTab('users'); }}>
          {our.email}
          {our.status && <span className="text-grayscale-1100 no-underline"> · {our.status}{our.is_trial ? ' (trial)' : ''}</span>}
        </button>
      );
    }
    if (our && our.user_missing) {
      return (
        <span className="text-red-600" title={our.user_id}>
          account deleted — ghost {String(our.user_id || '').slice(0, 8)}…
          {our.payer_email ? <span className="text-grayscale-1100"> ({our.payer_email})</span> : null}
          <span className="block text-[10px] text-grayscale-1100">see Payments → Orphaned payments</span>
        </span>
      );
    }
    if (notesUser) {
      return notesUser.missing
        ? <span className="text-red-600" title={notesUser.id}>not in our DB — notes name a deleted account</span>
        : <span className="text-amber-700" title={notesUser.id}>no row, but notes name {notesUser.email}</span>;
    }
    return <span className="text-red-600 font-medium">not in our DB — orphan</span>;
  };

  const gwRows = (kind: GwKind): any[] => {
    const rows: any[] = gw[kind]?.rows || [];
    const q = gwQ.trim().toLowerCase();
    return q ? rows.filter(r => gwHaystack(r).includes(q)) : rows;
  };

  const TabBtn: React.FC<{ id: Tab; label: string }> = ({ id, label }) => (
    <button onClick={() => setTab(id)}
      className={`py-2 px-4 text-sm font-grotesk whitespace-nowrap ${tab === id
        ? 'border-b-2 border-primary-700 text-primary-700 font-bold'
        : 'text-grayscale-1200'}`}>{label}</button>
  );

  return (
    <div className={embedded ? '' : 'min-h-screen bg-grayscale-100 p-4 md:p-8'}>
      <div className={embedded ? 'flex flex-col gap-4' : 'max-w-6xl mx-auto flex flex-col gap-4'}>
        {!embedded && (
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h1 className="text-2xl font-bold text-grayscale-1300 font-grotesk">Billing admin</h1>
              <div className="text-xs text-grayscale-1100">Signed in as {adminEmail} · <a className="underline" href="/">back to Cosmos</a></div>
            </div>
            <div className="text-xs text-grayscale-1100">
              {Object.entries(counts).map(([k, v]) => `${k}: ${v}`).join(' · ') || ''}
            </div>
          </div>
        )}

        <div className="flex border-b border-grayscale-300 overflow-x-auto">
          <TabBtn id="users" label="Users & subscriptions" />
          <TabBtn id="trials" label="Cancellations & refunds" />
          <TabBtn id="plans" label="Plans & pricing" />
          <TabBtn id="promos" label="Promotions & trials" />
          <TabBtn id="prompts" label="Upgrade prompts" />
          <TabBtn id="coupons" label="Coupons" />
          <TabBtn id="payments" label="Payments" />
          <TabBtn id="razorpay" label="Razorpay" />
          <TabBtn id="gateway" label="Gateway" />
          <TabBtn id="emails" label="Emails" />
        </div>

        {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
        {msg && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{msg}</div>}

        {tab === 'users' && (
          <div className="flex flex-col gap-4">
            {/* Global pay-once switch — ORs with the per-user flags below, so
                turning it on opens the option for everyone without clearing
                anyone's individual grant. */}
            <Card>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-bold text-grayscale-1300 font-grotesk">
                    One-time "pay once, no commitment" for all users
                  </div>
                  <div className="text-xs text-grayscale-1100">
                    {globalPayOnce
                      ? 'On — every user sees the pay-once option alongside auto-renew.'
                      : 'Off — only users individually enabled below see it.'}
                  </div>
                </div>
                <button
                  onClick={async () => {
                    const next = !globalPayOnce;
                    const r = await apiService.adminBillingSetGlobalPayOnce(userId, next);
                    if (r.success && r.data?.success) { flash(`Pay-once ${next ? 'enabled' : 'disabled'} for all users`); loadUsers(); }
                    else fail(r.error || 'Failed');
                  }}
                  className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${
                    globalPayOnce ? 'bg-primary-500' : 'bg-grayscale-400'}`}
                  aria-pressed={globalPayOnce}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                    globalPayOnce ? 'left-6' : 'left-0.5'}`} />
                </button>
              </div>
            </Card>

            <Card>
              <div className="flex gap-2 mb-3 flex-wrap">
                <input value={search} onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && loadUsers(search, 0)}
                  placeholder="Search email or name…"
                  className="h-9 px-2.5 rounded-md border-primary-300 border-[0.5px] bg-white text-sm w-72" />
                <Btn onClick={() => loadUsers(search, 0)}>Search</Btn>
                <Btn subtle onClick={() => setGrant({ email: '', plan_code: 'voyager', months: 12, note: '' })}>+ Manual subscription</Btn>
              </div>
              <div className="mb-3">
                <TagFilter value={tagF}
                  onChange={(v, params) => { setTagF(v); setTagParams(params); loadUsers(search, 0, params); }} />
                {tagFilterActive(tagF) && (
                  <div className="text-[11px] text-grayscale-1100 mt-1">
                    {pageInfo.total.toLocaleString()} account{pageInfo.total === 1 ? '' : 's'} match the lifecycle / tag filter
                    {search ? ' and the search' : ''}.
                  </div>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <UserHead />
                  <tbody>{users.map(u => <UserRow key={u.user_id} u={u} />)}</tbody>
                </table>
              </div>

              {/* Pagination — 10 per page against a few thousand users. */}
              <div className="flex items-center justify-between mt-3 text-xs text-grayscale-1100">
                <span>
                  {pageInfo.total > 0
                    ? `${page * pageInfo.per_page + 1}–${Math.min((page + 1) * pageInfo.per_page, pageInfo.total)} of ${pageInfo.total.toLocaleString()}`
                    : 'No users'}
                </span>
                <div className="flex items-center gap-2">
                  <Btn subtle disabled={page <= 0} onClick={() => loadUsers(search, page - 1)}>← Prev</Btn>
                  <span>Page {page + 1} of {pageInfo.pages}</span>
                  <Btn subtle disabled={page + 1 >= pageInfo.pages} onClick={() => loadUsers(search, page + 1)}>Next →</Btn>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Its own tab rather than a card under Payments: the owner's ask was
            to see the cancellations WITH whether people even clicked, and
            that is four things read together — the funnel, the reasons, the
            click log and the trials they came from. Since 2026-09 the same
            sheet ends paid renewals too, so the funnel comes split by kind
            (funnel.trial / funnel.subscription) and each log row says which
            exit it was. */}
        {tab === 'trials' && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs text-grayscale-1100">
                Every exit step is logged — opening the sheet, keeping the trial
                or membership, confirming — so "did anyone even click it" has
                an answer.
                {(['trial', 'subscription'] as const).map(k => {
                  const f = trials?.funnel?.[k];
                  return f && f.opened !== f.opened_users
                    ? <React.Fragment key={k}> {k === 'trial' ? 'Trial' : 'Membership'} sheet
                        opened {f.opened} times in total.</React.Fragment>
                    : null;
                })}
              </div>
              {/* Not `onClick={loadTrials}`: it takes the segment as its
                  first argument now, and the click event would be sent as
                  one. */}
              <Btn subtle disabled={trialsBusy} onClick={() => loadTrials()}>
                {trialsBusy ? 'Loading…' : 'Refresh'}
              </Btn>
            </div>

            {/* Two rows of six rather than one of twelve: each row is a
                funnel read left to right, and mixing the two would put
                "active trials" next to "renewals cancelled". The last two
                tiles are the win-back offer's own funnel: cancels that were
                handed the coupon, and how many of those came back with it. */}
            <div>
              <div className="text-[11px] font-bold tracking-widest text-grayscale-1100 uppercase mb-1.5">Trials</div>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <Stat label="Active trials" value={trials?.funnel?.trial?.active}
                  hint="Trial rows that are active and have not reached their end date" />
                <Stat label="Opened cancel sheet (users)" value={trials?.funnel?.trial?.opened_users}
                  hint="Distinct users who opened the cancel-trial sheet at least once" />
                <Stat label="Kept trial" value={trials?.funnel?.trial?.kept}
                  hint="Closed the sheet without cancelling" />
                <Stat label="Cancelled" value={trials?.funnel?.trial?.confirmed}
                  hint="Confirmed the trial cancellation" />
                <Stat label="Offered coupon" value={trials?.funnel?.trial?.winback_offered}
                  hint="Cancellations that were handed the win-back code (on screen and in the e-mail)" />
                <Stat label="Redeemed" value={trials?.funnel?.trial?.winback_redeemed}
                  hint="Of those, how many later paid with that code" />
              </div>
            </div>
            <div>
              <div className="text-[11px] font-bold tracking-widest text-grayscale-1100 uppercase mb-1.5">Memberships</div>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <Stat label="Renewing" value={trials?.funnel?.subscription?.active}
                  hint="Paid memberships still set to auto-renew — the ones that could yet be cancelled" />
                <Stat label="Opened cancel sheet (users)" value={trials?.funnel?.subscription?.opened_users}
                  hint="Distinct members who opened the cancel-membership sheet at least once" />
                <Stat label="Kept membership" value={trials?.funnel?.subscription?.kept}
                  hint="Closed the sheet without cancelling" />
                <Stat label="Renewal cancelled" value={trials?.funnel?.subscription?.confirmed}
                  hint="Confirmed — access runs to the paid period's end, then stops" />
                <Stat label="Offered coupon" value={trials?.funnel?.subscription?.winback_offered}
                  hint="Renewal cancellations that were handed the win-back code" />
                <Stat label="Redeemed" value={trials?.funnel?.subscription?.winback_redeemed}
                  hint="Of those, how many later paid with that code" />
              </div>
            </div>
            {/* Money that went back. One tile, because the count is only
                the headline: the total is what the owner reads, and a failed
                one is a customer still waiting. */}
            <div>
              <div className="text-[11px] font-bold tracking-widest text-grayscale-1100 uppercase mb-1.5">Refunds</div>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <Stat label="Refunds issued" value={trials?.funnel?.refunds?.count}
                  hint="Refunds Razorpay accepted, from either the Users or the Payments tab"
                  extra={trials?.funnel?.refunds && (
                    <>
                      {amountsByCurrency(trials.funnel.refunds.amount_by_currency)}
                      {trials.funnel.refunds.failed > 0 && (
                        <span className="text-red-600"> · {trials.funnel.refunds.failed} failed</span>
                      )}
                    </>
                  )} />
              </div>
            </div>

            {/* The win-back offer itself. Lives here, not on Promotions,
                because it is read against the two funnels above: "we offer
                20% to everyone who leaves — does anyone take it?" is a
                question about cancellations. Only WHICH promotion and WHEN
                is chosen here; what the code is worth (discount, the
                Razorpay offer per payment method, validity) stays with the
                promotion on its own tab, so the same code keeps working the
                same way whether typed at checkout or handed out here. */}
            <Card>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-bold text-grayscale-1300 font-grotesk">Win-back offer</div>
                  {/* Describes what is STORED, not the draft below: the
                      toggle acts on the live setting, and reading the
                      unsaved form here would say "on, TRIAL20" for a code
                      that was never saved. */}
                  <div className="text-xs text-grayscale-1100">
                    {(() => {
                      const live = winback?.settings;
                      if (!live) return 'Loading…';
                      if (!live.enabled) return 'Off — cancellations get no coupon.';
                      if (!live.promo_code) return 'On — but no code is chosen, so nothing is offered.';
                      const when = (live.apply_to || [])
                        .map((k: string) => k === 'subscription' ? 'a membership' : 'a trial')
                        .join(' or ');
                      return when
                        ? `On — ${live.promo_code} is shown after ${when} is cancelled, and goes out in the cancellation e-mail.`
                        : `On — but ${live.promo_code} applies to neither trials nor memberships, so nothing is offered.`;
                    })()}
                  </div>
                </div>
                <button disabled={!winbackForm || winbackBusy}
                  onClick={() => saveWinback({ enabled: !winbackForm?.enabled })}
                  className={`relative w-12 h-6 rounded-full transition-colors shrink-0 disabled:opacity-40 ${
                    winbackForm?.enabled ? 'bg-primary-500' : 'bg-grayscale-400'}`}
                  aria-pressed={!!winbackForm?.enabled} title="Enabled">
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                    winbackForm?.enabled ? 'left-6' : 'left-0.5'}`} />
                </button>
              </div>

              {winbackForm && (
                <div className="mt-3 pt-3 border-t border-grayscale-200 flex flex-col gap-3">
                  <div className="flex flex-wrap items-end gap-4">
                    <label className="text-xs flex flex-col gap-1">
                      <span className="text-grayscale-1100">Coupon</span>
                      {/* Only live coded promotions are offered: an auto-apply
                          campaign needs no code, and a code nobody can type at
                          checkout is a broken promise in an e-mail. */}
                      <select value={winbackForm.promo_code || ''}
                        onChange={e => setWinbackForm((f: any) => ({ ...f, promo_code: e.target.value }))}
                        className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 text-sm bg-white min-w-[14rem]">
                        <option value="">— none —</option>
                        {(winback?.candidates || []).map((c: any) => (
                          <option key={c.code} value={c.code}>
                            {c.code} — {c.discount_label}{c.name && c.name !== c.code ? ` (${c.name})` : ''}
                          </option>
                        ))}
                        {/* A code that is set but no longer a live candidate
                            (deactivated or expired since) must still show, or
                            the select would silently display the first option
                            as if that were what is stored. */}
                        {winbackForm.promo_code
                          && !(winback?.candidates || []).some((c: any) => c.code === winbackForm.promo_code) && (
                          <option value={winbackForm.promo_code}>{winbackForm.promo_code} — not live</option>
                        )}
                      </select>
                    </label>
                    <div className="text-xs flex flex-col gap-1">
                      <span className="text-grayscale-1100">Offer it when cancelling</span>
                      <div className="flex items-center gap-4 h-9">
                        {([['trial', 'Trials'], ['subscription', 'Memberships']] as const).map(([k, lbl]) => (
                          <label key={k} className="flex items-center gap-1.5 cursor-pointer">
                            <input type="checkbox"
                              checked={(winbackForm.apply_to || []).includes(k)}
                              onChange={e => setWinbackForm((f: any) => {
                                const cur: string[] = (f.apply_to || []).filter((x: string) => x !== k);
                                return { ...f, apply_to: e.target.checked ? [...cur, k] : cur };
                              })} />
                            {lbl}
                          </label>
                        ))}
                      </div>
                    </div>
                    <label className="text-xs flex items-center gap-1.5 h-9 cursor-pointer"
                      title="Adds each canceller's e-mail to the promotion's eligible list, so nobody who merely heard the code can use it. Only takes effect when that list is already non-empty — an empty list means 'anyone', and locking it to the first canceller would shut everyone else out.">
                      <input type="checkbox" checked={!!winbackForm.restrict_to_cancellers}
                        onChange={e => setWinbackForm((f: any) => ({ ...f, restrict_to_cancellers: e.target.checked }))} />
                      Only cancellers can redeem
                      <span className="text-grayscale-1100">(needs a non-empty eligible list on the promotion)</span>
                    </label>
                  </div>

                  <label className="text-xs flex flex-col gap-1">
                    <span className="text-grayscale-1100">
                      Message shown after the cancel and in the e-mail —
                      <span className="font-mono"> {'{code}'}</span> and
                      <span className="font-mono"> {'{discount}'}</span> are filled in
                    </span>
                    <textarea value={winbackForm.message || ''} rows={2} maxLength={500}
                      onChange={e => setWinbackForm((f: any) => ({ ...f, message: e.target.value }))}
                      className="w-full px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm resize-none" />
                  </label>

                  {/* What the chosen code is actually worth right now, read
                      from the promotion — so an admin can see "20%, card +
                      UPI, once per user" without leaving the tab, and a code
                      that has gone dead is called out where it is chosen.
                      The full summary is only for the SAVED code (it is the
                      server's match for that); a freshly picked candidate
                      shows its discount and asks for a Save. */}
                  {winbackForm.promo_code && (
                    winback?.promotion && winback.promotion.code === winbackForm.promo_code ? (
                      <div className="text-xs text-grayscale-1200 flex flex-wrap gap-x-3 gap-y-1">
                        <span className="font-mono font-bold text-grayscale-1300">{winback.promotion.code}</span>
                        {winback.promotion.name && winback.promotion.name !== winback.promotion.code && (
                          <span>{winback.promotion.name}</span>
                        )}
                        <span>{winback.promotion.discount_type === 'percent'
                          ? `${winback.promotion.discount_value}% off`
                          : `${inr(winback.promotion.discount_value)} off`}</span>
                        {/* The summary carries `methods` (the rails the
                            promotion has a Razorpay offer for — card, UPI)
                            and `has_offer`; a promotion with one legacy
                            offer id and no per-method map has has_offer
                            without methods, and one with neither can only
                            discount a one-time payment. */}
                        <span>{(() => {
                          const p = winback.promotion;
                          const methods: string[] = Array.isArray(p.methods) ? p.methods
                            : (p.offer_ids && typeof p.offer_ids === 'object' ? Object.keys(p.offer_ids) : []);
                          const names = methods.map((m: string) => m === 'upi' ? 'UPI' : m).join(' / ');
                          return names ? `pays by ${names}`
                            : p.has_offer ? 'pays by any method (one Razorpay offer)'
                            : 'one-time only (no Razorpay offer attached)';
                        })()}</span>
                        {winback.promotion.once_per_user && <span>once per user</span>}
                        <span>{winback.promotion.eligible_count
                          ? `${winback.promotion.eligible_count} eligible e-mail${winback.promotion.eligible_count === 1 ? '' : 's'}`
                          : 'anyone with the code'}</span>
                        <span className={winback.promotion.is_active ? 'text-green-700' : 'text-red-600'}>
                          {winback.promotion.is_active ? 'live' : 'inactive — nothing will be offered'}
                        </span>
                      </div>
                    ) : (() => {
                      const picked = (winback?.candidates || [])
                        .find((c: any) => c.code === winbackForm.promo_code);
                      return picked ? (
                        <div className="text-xs text-grayscale-1200">
                          <span className="font-mono font-bold text-grayscale-1300">{picked.code}</span>
                          {' '}— {picked.discount_label} · save to make it the offer
                        </div>
                      ) : (
                        <div className="text-xs text-red-600">
                          This code is not a live coded promotion — nothing will be offered until it is.
                        </div>
                      );
                    })()
                  )}

                  <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
                    <div className="text-xs text-grayscale-1100 flex items-center gap-2 flex-wrap">
                      Edit the discount, Razorpay offer ids (card / UPI) or validity on the Promotions tab.
                      <Btn subtle onClick={() => setTab('promos')}>Open Promotions</Btn>
                    </div>
                    <Btn disabled={winbackBusy} onClick={() => saveWinback({
                      enabled: !!winbackForm.enabled,
                      promo_code: winbackForm.promo_code || '',
                      apply_to: winbackForm.apply_to || [],
                      restrict_to_cancellers: !!winbackForm.restrict_to_cancellers,
                      message: winbackForm.message || '',
                    })}>{winbackBusy ? 'Saving…' : 'Save'}</Btn>
                  </div>
                </div>
              )}
            </Card>

            <Card title="Cancellations (why people left)">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-grayscale-1100 border-b border-grayscale-300">
                    <th className="py-2 pr-3">Date</th><th className="pr-3">Kind</th><th className="pr-3">User</th>
                    <th className="pr-3">Plan</th>
                    <th className="pr-3">Reasons</th><th className="pr-3">Other</th>
                    <th className="pr-3">By</th>
                    <th className="pr-3">Coupon</th><th className="pr-3">Redeemed</th>
                    <th className="pr-3">Gateway</th>
                  </tr></thead>
                  <tbody>{(trials?.cancellations || []).map((c: any) => (
                    <tr key={c.id} className="border-b border-grayscale-200">
                      <td className="py-2 pr-3 whitespace-nowrap">{fmtWhen(c.created_at)}</td>
                      <td className="pr-3">{kindLabel(c.kind)}</td>
                      <td className="pr-3">{c.email || c.user_id}</td>
                      <td className="pr-3">{c.plan_code || '—'}</td>
                      <td className="pr-3">{(c.reasons || []).join(', ') || '—'}</td>
                      <td className="pr-3 max-w-xs">{c.other_text || '—'}</td>
                      {/* "user" when they did it themselves from the sheet;
                          otherwise the admin whose Cancel button it was. */}
                      <td className="pr-3 text-grayscale-1100 whitespace-nowrap">{cancelledBy(c)}</td>
                      {/* The come-back code this cancel was handed, and the
                          day they paid with it — green because that is the
                          one row on this table where the money came back.
                          A dash under Coupon is a cancel from before the
                          offer existed or while it was switched off. */}
                      <td className="pr-3 font-mono text-xs" title={c.winback_offered_at
                          ? `Offered ${fmtWhen(c.winback_offered_at)}` : undefined}>
                        {c.winback_code || '—'}
                      </td>
                      <td className="pr-3 whitespace-nowrap">
                        {c.winback_redeemed_at
                          ? <span className="text-green-700">{fmtDate(c.winback_redeemed_at)}</span>
                          : '—'}
                      </td>
                      {/* "failed" is the row that needs a human: the customer
                          is still on a mandate that will charge at trial end,
                          or on a subscription that will renew. 'none' for a
                          trial means no mandate was ever taken; for a
                          membership it means no gateway subscription was on
                          the row (a team-granted pack). */}
                      <td className="pr-3">{!c.gateway_result || c.gateway_result === 'none'
                        ? <span className="text-grayscale-1100">
                            {kindLabel(c.kind) === 'membership' ? 'nothing at gateway' : 'no mandate'}
                          </span>
                        : c.gateway_result.startsWith('failed')
                          ? <span className="text-red-600">{c.gateway_result} — cancel manually</span>
                          : <span className="text-green-700">{c.gateway_result}</span>}</td>
                    </tr>
                  ))}</tbody>
                </table>
                {!(trials?.cancellations || []).length && (
                  <div className="text-xs text-grayscale-1100 pt-2">
                    {trials ? 'No cancellations yet.' : trialsBusy ? 'Loading…' : '—'}
                  </div>
                )}
              </div>
            </Card>

            {/* Every refund attempted, with the reason the admin gave and
                what Razorpay said. Failed rows stay in the log on purpose:
                a refund that did not go through is money a customer is
                still owed, and the Payments tab alone would never show it. */}
            <Card title="Refunds">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-grayscale-1100 border-b border-grayscale-300">
                    <th className="py-2 pr-3">Date</th><th className="pr-3">User</th>
                    <th className="pr-3">Payment</th><th className="pr-3">Amount</th>
                    <th className="pr-3">Full / partial</th><th className="pr-3">Reason</th>
                    <th className="pr-3">By</th><th className="pr-3">Razorpay refund id</th>
                    <th className="pr-3">Status</th><th className="pr-3">End access</th>
                  </tr></thead>
                  <tbody>{(trials?.refunds || []).map((r: any) => (
                    <tr key={r.id} className="border-b border-grayscale-200">
                      <td className="py-2 pr-3 whitespace-nowrap">{fmtWhen(r.created_at)}</td>
                      <td className="pr-3">
                        {r.email || r.user_id}
                        {r.name && <span className="ml-2 text-xs text-grayscale-1100">{r.name}</span>}
                      </td>
                      <td className="pr-3 font-mono text-xs">{r.payment_id || '—'}</td>
                      <td className="pr-3 whitespace-nowrap">{money(r.amount_minor, r.currency || 'INR')}</td>
                      <td className="pr-3">{r.full_refund ? 'full' : 'partial'}</td>
                      <td className="pr-3 max-w-xs">{r.reason || '—'}</td>
                      <td className="pr-3 text-grayscale-1100">{r.refunded_by || '—'}</td>
                      <td className="pr-3 font-mono text-xs">{r.razorpay_refund_id || '—'}</td>
                      {/* The error rides on the status cell's title: it is
                          Razorpay's own sentence and can be long. */}
                      <td className="pr-3" title={r.error || undefined}>
                        {r.status === 'failed'
                          ? <span className="text-red-600">failed{r.error ? ' — ' + String(r.error).slice(0, 60) : ''}</span>
                          : <span className="text-green-700">{r.status || 'issued'}</span>}
                      </td>
                      <td className="pr-3">{r.end_access ? 'yes' : 'no'}</td>
                    </tr>
                  ))}</tbody>
                </table>
                {!(trials?.refunds || []).length && (
                  <div className="text-xs text-grayscale-1100 pt-2">
                    {trials ? 'No refunds yet.' : trialsBusy ? 'Loading…' : '—'}
                  </div>
                )}
              </div>
            </Card>

            <Card title="Cancel-sheet activity (every click)">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-grayscale-1100 border-b border-grayscale-300">
                    <th className="py-2 pr-3">Date</th><th className="pr-3">Kind</th><th className="pr-3">User</th>
                    <th className="pr-3">Event</th>
                    <th className="pr-3">Reasons</th><th className="pr-3">Other</th>
                  </tr></thead>
                  <tbody>{(trials?.events || []).map((e: any) => (
                    <tr key={e.id} className="border-b border-grayscale-200">
                      <td className="py-2 pr-3 whitespace-nowrap">{fmtWhen(e.created_at)}</td>
                      <td className="pr-3">{kindLabel(e.kind)}</td>
                      <td className="pr-3">
                        {e.email || e.user_id}
                        {e.name && <span className="ml-2 text-xs text-grayscale-1100">{e.name}</span>}
                      </td>
                      {/* Red is money gone, green is money kept; 'opened' is
                          neither yet, so it stays plain. */}
                      <td className="pr-3">
                        <span className={e.event === 'confirmed' ? 'text-red-600'
                          : e.event === 'kept' ? 'text-green-700' : 'text-grayscale-1200'}>
                          {e.event}
                        </span>
                      </td>
                      <td className="pr-3">{(e.reasons || []).join(', ') || '—'}</td>
                      <td className="pr-3 max-w-xs">{e.other_text || '—'}</td>
                    </tr>
                  ))}</tbody>
                </table>
                {!(trials?.events || []).length && (
                  <div className="text-xs text-grayscale-1100 pt-2">
                    {trials ? 'Nobody has opened the cancel sheet yet.' : trialsBusy ? 'Loading…' : '—'}
                  </div>
                )}
              </div>
            </Card>

            {/* Every trial row, with the Sep 2026 lifecycle beside it: who
                they said they were (the form), which state the row is in,
                when it lapsed and which lifecycle mails have gone. The kit's
                DataTable rather than the hand-built table above so the
                owner can filter, sort and pull a CSV of one segment. */}
            <Card title="Trials">
              <div className="flex items-center gap-2 flex-wrap mb-3 text-xs text-grayscale-1100">
                <span>Segment</span>
                <select value={trialSegment}
                  onChange={e => {
                    const seg = e.target.value as TrialSegment;
                    setTrialSegment(seg);
                    loadTrials(seg);
                  }}
                  className="h-8 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-xs">
                  {TRIAL_SEGMENTS.map(s => <option key={s} value={s}>{SEGMENT_LABEL[s]}</option>)}
                </select>
                <span>
                  {trialSegment === 'lapsed'
                    ? '— ran out unpaid: same access as Explorer, the trial button greyed for good.'
                    : trialSegment === 'converted'
                      ? '— paid during or after the trial.'
                      : trialSegment === 'active'
                        ? '— running now; the ending-soon mail goes at end −24h.'
                        : ''}
                </span>
              </div>
              {trialsBusy && !trials ? (
                <div className="text-xs text-grayscale-1100">Loading…</div>
              ) : (
                <DataTable
                  rows={(trials?.trials || []).map((t: any) => ({
                    ...t,
                    // Flattened for the CSV and the column filters; the
                    // renderers below still read the raw row.
                    user: t.email || t.user_id,
                    lifecycle_tags: lifecycleText(t),
                    organization: t.trial_form?.organization ?? t.organization ?? '',
                    designation: t.trial_form?.designation ?? t.designation ?? '',
                    state: t.state || '',
                  }))}
                  csvName={`trials_${trialSegment}`}
                  searchable
                  maxHeight="36rem"
                  columns={['user', 'lifecycle_tags', 'plan_code', 'source', 'status', 'state', 'organization',
                            'designation', 'current_start', 'current_end', 'lapsed_at',
                            'cancelled_at', 'trial_ending_email_at', 'trial_lapsed_email_at', 'note']}
                  labels={{
                    user: 'User', lifecycle_tags: 'Account now', plan_code: 'Plan', source: 'Source', status: 'Status',
                    state: 'State', organization: 'Organization', designation: 'Designation',
                    current_start: 'Started', current_end: 'Ends', lapsed_at: 'Lapsed at',
                    cancelled_at: 'Cancelled', trial_ending_email_at: 'Ending mail (attempted)',
                    trial_lapsed_email_at: 'Lapsed mail (attempted)', note: 'Note',
                  }}
                  empty={trials
                    ? (trialSegment === 'all' ? 'No trials yet.' : `No ${SEGMENT_LABEL[trialSegment].toLowerCase()} trials.`)
                    : '—'}
                  render={{
                    user: t => (
                      <>
                        {t.email || t.user_id}
                        {t.name && <span className="ml-2 text-xs text-grayscale-1100">{t.name}</span>}
                      </>
                    ),
                    // The ACCOUNT's lifecycle today (user_tags.decorate) —
                    // a lapsed trial row can belong to a paying Voyager now.
                    lifecycle_tags: t => <span className="inline-block min-w-[12rem]"><UserTagCells row={t} /></span>,
                    source: t => <span className="text-grayscale-1100" title={t.source || ''}>{trialSource(t)}</span>,
                    status: t => (
                      <span className={t.status === 'active' ? 'text-green-700'
                        : t.status === 'cancelled' ? 'text-amber-700' : 'text-grayscale-1100'}>
                        {t.status || '—'}
                      </span>
                    ),
                    // The server's per-row vocabulary (admin_trials: active /
                    // lapsed / converted / cancelled / superseded), not the
                    // account-level user_state words the Users tab gets.
                    state: t => t.state
                      ? <span className={t.state === 'active' ? 'text-green-700'
                          : t.state === 'lapsed' ? 'text-amber-700'
                          : t.state === 'converted' ? 'text-primary-700' : 'text-grayscale-1100'}>
                          {t.state}
                        </span>
                      : <span className="text-grayscale-1100">—</span>,
                    current_start: t => fmtDate(t.current_start),
                    current_end: t => fmtDate(t.current_end),
                    lapsed_at: t => fmtDate(t.lapsed_at),
                    cancelled_at: t => fmtDate(t.cancelled_at),
                    // A stamp means the mail was CLAIMED, and a claim is
                    // never re-sent even if the send then failed — so the
                    // time is the fact, "—" means not yet due, and the
                    // column says "attempted": a failed send is an audit
                    // row (trial_mail_failed) under the user, not a gap here.
                    trial_ending_email_at: t => t.trial_ending_email_at
                      ? <span title={fmtWhen(t.trial_ending_email_at)}>{fmtDate(t.trial_ending_email_at)}</span>
                      : <span className="text-grayscale-1100">—</span>,
                    trial_lapsed_email_at: t => t.trial_lapsed_email_at
                      ? <span title={fmtWhen(t.trial_lapsed_email_at)}>{fmtDate(t.trial_lapsed_email_at)}</span>
                      : <span className="text-grayscale-1100">—</span>,
                    note: t => <span className="text-grayscale-1100">{t.note || '—'}</span>,
                  }}
                />
              )}
            </Card>
          </div>
        )}

        {tab === 'plans' && (
          <div className="flex flex-col gap-4">
            {/* Foreign-currency recurring is a Razorpay account permission, not
                a setting we control — so the panel reports it and offers a
                re-check for the moment support switches it on. */}
            <Card>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-bold text-grayscale-1300 font-grotesk">
                    International subscriptions (recurring in USD)
                    <span className={`ml-2 text-[11px] px-2 py-0.5 rounded-full ${
                      usdCap?.available
                        ? 'bg-green-50 text-green-700 border border-green-600'
                        : 'bg-amber-50 text-amber-800 border border-amber-300'}`}>
                      {usdCap === null ? 'checking…' : usdCap.available ? 'enabled' : 'not enabled'}
                    </span>
                  </div>
                  <div className="text-xs text-grayscale-1100 mt-1 max-w-2xl">
                    {usdCap?.available
                      ? 'Overseas customers can subscribe with auto-renewal in USD. One-time payments still work as before.'
                      : <>Razorpay rejects non-INR subscription plans on this account
                          {usdCap?.error ? <> (<span className="font-mono">{usdCap.error}</span>)</> : null}.
                          Overseas customers are billed $99 as a one-time yearly payment instead.
                          Ask Razorpay support to enable <span className="font-medium">international
                          subscriptions</span>, then re-check — no deploy needed.</>}
                  </div>
                </div>
                <Btn subtle disabled={capBusy} onClick={recheckCapability}>
                  {capBusy ? 'Checking…' : 'Re-check'}
                </Btn>
              </div>
            </Card>

            <RegionMap userId={userId} flash={flash} fail={fail} />

          <Card title="Plans — price changes apply to NEW subscriptions (existing ones keep their price)">
            <div className="flex flex-col gap-3">
              {plans.map((p, i) => (
                <div key={p.code} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-center border-t border-grayscale-200 pt-3">
                  <div className="font-medium text-grayscale-1300">{p.code}</div>
                  <input className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 text-sm" value={p.name}
                    onChange={e => setPlans(ps => ps.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                  <div className="flex items-center gap-1 text-sm"
                    title="The fallback price, used wherever no region price is set below.">
                    ₹<input
                    className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 text-sm w-24" value={p._price_inr}
                    onChange={e => setPlans(ps => ps.map((x, j) => j === i ? { ...x, _price_inr: e.target.value } : x))} />/yr</div>
                  <input className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 text-sm md:col-span-2" value={p.description}
                    onChange={e => setPlans(ps => ps.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} />
                  {/* Feature rows shown in the pricing comparison modal.
                      "Name: value" per line — blank value drops the row. */}
                  <textarea rows={3} placeholder="Cosmos: Full access"
                    className="h-auto px-2 py-1.5 rounded-md border-[0.5px] border-primary-300 text-xs font-mono md:col-span-5"
                    value={p._features_text ?? Object.entries(p.features || {}).map(([k, v]) => `${k}: ${v}`).join('\n')}
                    onChange={e => setPlans(ps => ps.map((x, j) => j === i ? { ...x, _features_text: e.target.value } : x))} />
                  <div className="flex gap-2 items-center">
                    <label className="text-xs flex items-center gap-1">
                      <input type="checkbox" checked={p.is_active}
                        onChange={e => setPlans(ps => ps.map((x, j) => j === i ? { ...x, is_active: e.target.checked } : x))} /> active
                    </label>
                    <Btn onClick={async () => {
                      const rupees = parseFloat(p._price_inr);
                      if (isNaN(rupees) || rupees < 0) { fail('Invalid price'); return; }
                      const features: Record<string, string> = {};
                      const raw = p._features_text ??
                        Object.entries(p.features || {}).map(([k, v]) => `${k}: ${v}`).join('\n');
                      raw.split('\n').forEach((line: string) => {
                        const idx = line.indexOf(':');
                        if (idx <= 0) return;
                        const k = line.slice(0, idx).trim();
                        const v = line.slice(idx + 1).trim();
                        if (k && v) features[k] = v;
                      });
                      const r = await apiService.adminBillingUpdatePlan(userId, p.code, {
                        name: p.name, description: p.description,
                        price_inr_paise: Math.round(rupees * 100), is_active: p.is_active,
                        features,
                      });
                      if (r.success && r.data?.success) { flash(`${p.code} saved`); loadPlans(); }
                      else fail(r.error || 'Save failed');
                    }}>Save</Btn>{' '}
                    <Btn subtle onClick={async () => {
                      // The server refuses while any subscription references the
                      // plan and tells you to retire it instead, so this can only
                      // ever remove a plan nobody is on.
                      if (!window.confirm(
                        `Delete the "${p.code}" plan permanently?\n\n`
                        + 'Refused if anyone is subscribed to it — untick "active" '
                        + 'to retire it instead, which keeps existing subscribers.')) return;
                      const r = await apiService.adminBillingDeletePlan(userId, p.code);
                      if (r.success && r.data?.success) { flash(`${p.code} deleted`); loadPlans(); }
                      else fail(r.error || 'Delete failed');
                    }}>Delete</Btn>
                  </div>
                  <RegionPrices userId={userId} planCode={p.code}
                    legacyInrPaise={p.price_inr_paise || 0} flash={flash} fail={fail} />
                </div>
              ))}
            </div>

            {/* Add a plan */}
            <div className="border-t border-grayscale-200 mt-4 pt-3 flex flex-col gap-2">
              <div className="text-sm font-medium text-grayscale-1300">Add a plan</div>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-center">
                <input className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 text-sm"
                  placeholder="code (e.g. pioneer)" value={newPlan.code}
                  onChange={e => setNewPlan(n => ({ ...n, code: e.target.value }))} />
                <input className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 text-sm"
                  placeholder="Name" value={newPlan.name}
                  onChange={e => setNewPlan(n => ({ ...n, name: e.target.value }))} />
                <div className="flex items-center gap-1 text-sm">₹<input
                  className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 text-sm w-24"
                  placeholder="0" value={newPlan.price}
                  onChange={e => setNewPlan(n => ({ ...n, price: e.target.value }))} />/yr</div>
                <input className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 text-sm md:col-span-2"
                  placeholder="Description shown on the plan card" value={newPlan.description}
                  onChange={e => setNewPlan(n => ({ ...n, description: e.target.value }))} />
                <Btn onClick={async () => {
                  const rupees = Number(newPlan.price || 0);
                  if (!Number.isFinite(rupees) || rupees < 0) { fail('Price must be a number'); return; }
                  const r = await apiService.adminBillingCreatePlan(userId, {
                    code: newPlan.code.trim().toLowerCase(),
                    name: newPlan.name.trim(),
                    description: newPlan.description.trim(),
                    price_inr_paise: Math.round(rupees * 100),
                  });
                  if (r.success && r.data?.success) {
                    flash(`${newPlan.code} created`);
                    setNewPlan({ code: '', name: '', price: '', description: '' });
                    loadPlans();
                  } else fail(r.error || 'Create failed');
                }}>Add plan</Btn>
              </div>
              <p className="text-xs text-grayscale-1100">
                The code is the identifier every subscription row points at and cannot be
                changed later. New plans start active; add feature lines by saving the plan
                once it exists.
              </p>
            </div>
          </Card>
          </div>
        )}

        {tab === 'promos' && (
          <div className="flex flex-col gap-4">
            {/* Free-tier paywall. One switch so the lock can be lifted for a
                launch or a demo without a deploy. */}
            {lockCfg && (
              <Card>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-sm font-bold text-grayscale-1300 font-grotesk">
                      Free-tier lock (search, filters, introductions)
                    </div>
                    <div className="text-xs text-grayscale-1100">
                      {lockCfg.enabled
                        ? `On — free accounts see ${lockCfg.preview_results} preview results and hit the pricing modal on locked actions.`
                        : 'Off — everyone has full access regardless of subscription.'}
                    </div>
                  </div>
                  <button
                    onClick={() => saveLock({ enabled: !lockCfg.enabled })}
                    className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${
                      lockCfg.enabled ? 'bg-primary-500' : 'bg-grayscale-400'}`}
                    aria-pressed={lockCfg.enabled}>
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                      lockCfg.enabled ? 'left-6' : 'left-0.5'}`} />
                  </button>
                </div>
                {lockCfg.enabled && (
                  <div className="flex flex-wrap gap-4 mt-3 pt-3 border-t border-grayscale-200">
                    {([['can_search', 'Search'], ['can_filter', 'Filters'],
                       ['can_contact', 'Request introduction']] as const).map(([k, lbl]) => (
                      <label key={k} className="text-xs flex items-center gap-1.5">
                        <input type="checkbox" checked={!!lockCfg[k]}
                          onChange={e => saveLock({ [k]: e.target.checked })} />
                        {lbl} allowed on free
                      </label>
                    ))}
                    <label className="text-xs flex items-center gap-1.5">
                      Preview results
                      <input type="number" min={0} max={50} value={lockCfg.preview_results}
                        onChange={e => saveLock({ preview_results: Number(e.target.value) })}
                        className="h-8 px-2 rounded-md border-[0.5px] border-primary-300 text-xs w-16" />
                    </label>
                  </div>
                )}
              </Card>
            )}

            {/* Self-serve trial for everyone */}
            <Card>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-bold text-grayscale-1300 font-grotesk">Free trial for all users</div>
                  <div className="text-xs text-grayscale-1100">
                    {trialCfg.enabled
                      ? `On — anyone can start a ${trialCfg.days}-day ${trialCfg.plan_code} trial once, no card needed.`
                      : 'Off — no trial button is shown to users.'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="number" min={1} max={365} value={trialCfg.days}
                    onChange={e => setTrialCfg((c: any) => ({ ...c, days: Number(e.target.value) }))}
                    className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 text-sm w-20" />
                  <span className="text-xs text-grayscale-1100">days of</span>
                  <select value={trialCfg.plan_code}
                    onChange={e => setTrialCfg((c: any) => ({ ...c, plan_code: e.target.value }))}
                    className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 text-sm">
                    {(plans.length ? plans : [{ code: 'voyager' }, { code: 'navigator' }]).map((p: any) =>
                      <option key={p.code} value={p.code}>{p.code}</option>)}
                  </select>
                  <Btn onClick={async () => {
                    const next = { ...trialCfg, enabled: trialCfg.enabled };
                    const r = await apiService.adminBillingTrialSettings(userId, next);
                    if (r.success && r.data?.success) { setTrialCfg(r.data.trial); flash('Trial settings saved'); }
                    else fail(r.error || 'Save failed');
                  }}>Save</Btn>
                  <button
                    onClick={async () => {
                      const next = { ...trialCfg, enabled: !trialCfg.enabled };
                      const r = await apiService.adminBillingTrialSettings(userId, next);
                      if (r.success && r.data?.success) { setTrialCfg(r.data.trial); flash(`Trials ${next.enabled ? 'enabled' : 'disabled'}`); }
                      else fail(r.error || 'Failed');
                    }}
                    className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${
                      trialCfg.enabled ? 'bg-primary-500' : 'bg-grayscale-400'}`}
                    aria-pressed={trialCfg.enabled}>
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                      trialCfg.enabled ? 'left-6' : 'left-0.5'}`} />
                  </button>
                </div>
              </div>
            </Card>

            {/* The sweep behind the card-free trial: lapses trials at their
                end, mails the conversion nudge at end −12h and the follow-up
                at end +24h. Its own card so the owner can see it is alive —
                a trial that never lapses is full access for free. */}
            <Card>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-bold text-grayscale-1300 font-grotesk">Trial lifecycle</div>
                  <div className="text-xs text-grayscale-1100">
                    Lapses trials at their end date and sends the ending-soon and lapsed follow-up mails.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Btn subtle onClick={() => loadLifecycle()}>Refresh</Btn>
                  <Btn disabled={!!lifecycleBusy} onClick={runLifecycleNow}
                    title="Lapse due trials and send due mails now, as you">
                    {lifecycleBusy === 'run' ? 'Running…' : 'Run sweep now'}
                  </Btn>
                </div>
              </div>
              {lifecycleErr && !lifecycle && (
                <div className="text-xs text-red-600 mt-2">{lifecycleErr}</div>
              )}
              {lifecycle && (() => {
                const st = lifecycle;
                const lastRun = st.last_run;
                const res = lastRun?.result || {};
                // Whatever counts the status carries beyond the fixed keys —
                // active / due to lapse / ending soon due — shown generically
                // so the panel is not tied to the server's exact names.
                const skip = new Set(['scheduler_running', 'env_disabled', 'started_at', 'last_run',
                                      'next_run_estimate', 'settings']);
                const counts = Object.entries(st)
                  .filter(([k, v]) => !skip.has(k) && typeof v === 'number');
                return (
                  <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-grayscale-200 text-xs text-grayscale-1200">
                    <div>
                      Scheduler:{' '}
                      {st.scheduler_running
                        ? <span className="text-green-700 font-medium">running</span>
                        : <span className="text-red-600 font-medium">not running</span>}
                      {st.started_at && <span className="text-grayscale-1100"> since {fmtWhen(st.started_at)}</span>}
                      {st.settings && st.settings.auto === false && (
                        <span className="text-amber-700"> — automatic sweeps are off; use Run sweep now.</span>
                      )}
                      {!st.scheduler_running && (
                        <span className="text-red-600"> — nothing lapses on its own (TRIAL_LIFECYCLE_AUTO=0 or the API did not start it).</span>
                      )}
                    </div>
                    <div>
                      Last run:{' '}
                      {lastRun?.at ? (
                        <>
                          {fmtWhen(lastRun.at)} by <b>{lastRun.by || 'auto'}</b>
                          {res.skipped
                            ? <span className="text-grayscale-1100"> — skipped ({String(res.skipped)})</span>
                            : <> — lapsed {res.lapsed ?? 0}, ending-soon mails {res.ending_sent ?? 0},
                                lapsed mails {res.lapsed_sent ?? 0}
                                {/* Claimed but refused by SendGrid: stamped, never
                                    retried, so the only place to see it is here
                                    and in the audit trail. */}
                                {(res.send_failed ?? 0) > 0 && <span className="text-red-600">, sends failed {res.send_failed}</span>}
                                {(res.errors ?? 0) > 0 && <span className="text-red-600">, errors {res.errors}</span>}</>}
                        </>
                      ) : <span className="text-grayscale-1100">none since the API started</span>}
                    </div>
                    <div>
                      Next run: {st.next_run_estimate
                        ? fmtWhen(st.next_run_estimate)
                        : <span className="text-grayscale-1100">—</span>}
                      {st.settings && (
                        <span className="text-grayscale-1100">
                          {' '}(first sweep {st.settings.startup_delay_minutes} min after start, then every {st.settings.interval_minutes} min)
                        </span>
                      )}
                    </div>
                    {counts.length > 0 && (
                      <div className="flex gap-3 flex-wrap">
                        {counts.map(([k, v]) => (
                          <span key={k} className="rounded-full border border-grayscale-300 bg-white px-2.5 py-0.5">
                            <b className="text-grayscale-1300">{String(v)}</b> {k.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    )}
                    {lifecycleForm && (
                      <div className="flex items-center gap-2 flex-wrap pt-1">
                        <label className="flex items-center gap-1.5">
                          <input type="checkbox" checked={lifecycleForm.auto !== false}
                            onChange={e => setLifecycleForm((f: any) => ({ ...f, auto: e.target.checked }))} />
                          Automatic
                        </label>
                        <span className="text-grayscale-1100">·</span>
                        <span>Every</span>
                        <input type="number" min={1} max={1440} value={lifecycleForm.interval_minutes ?? 15}
                          onChange={e => setLifecycleForm((f: any) => ({ ...f, interval_minutes: Number(e.target.value) }))}
                          className="h-8 px-2 rounded-md border-[0.5px] border-primary-300 text-xs w-16" />
                        <span>min</span>
                        <span className="text-grayscale-1100">·</span>
                        <span>Ending-soon mail</span>
                        <input type="number" min={1} max={168} value={lifecycleForm.ending_soon_hours_before ?? 24}
                          onChange={e => setLifecycleForm((f: any) => ({ ...f, ending_soon_hours_before: Number(e.target.value) }))}
                          className="h-8 px-2 rounded-md border-[0.5px] border-primary-300 text-xs w-16" />
                        <span>h before the end</span>
                        <span className="text-grayscale-1100">·</span>
                        <span>Follow-up mail</span>
                        <input type="number" min={1} max={720} value={lifecycleForm.lapsed_followup_hours_after ?? 24}
                          onChange={e => setLifecycleForm((f: any) => ({ ...f, lapsed_followup_hours_after: Number(e.target.value) }))}
                          className="h-8 px-2 rounded-md border-[0.5px] border-primary-300 text-xs w-16" />
                        <span>h after it lapses</span>
                        <Btn disabled={!!lifecycleBusy} onClick={() => saveLifecycle({
                          auto: lifecycleForm.auto !== false,
                          interval_minutes: Number(lifecycleForm.interval_minutes ?? 15),
                          ending_soon_hours_before: Number(lifecycleForm.ending_soon_hours_before ?? 24),
                          lapsed_followup_hours_after: Number(lifecycleForm.lapsed_followup_hours_after ?? 24),
                        })}>{lifecycleBusy === 'save' ? 'Saving…' : 'Save'}</Btn>
                      </div>
                    )}
                    <p className="text-grayscale-1100">
                      Each mail is stamped on the trial row before it is sent and never re-sent; the
                      ending-soon mail is skipped on trials shorter than two days.
                    </p>
                  </div>
                );
              })()}
            </Card>

            <Card>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-bold text-grayscale-1300 font-grotesk">Promotions</div>
                <Btn onClick={() => setPromoForm({
                  name: '', description: '', promo_type: 'discount', auto_apply: true,
                  code: '', discount_type: 'percent', discount_value: 10,
                  starts_at: '', ends_at: '', plan_codes: [], intro_cycles: 0,
                  razorpay_offer_id: '', max_redemptions: '', is_active: true,
                  eligible_emails: '', eligible_signup_before: '', offer_ids: {},
                })}>+ New promotion</Btn>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-grayscale-1100 border-b border-grayscale-300">
                  <th className="py-2 pr-3">Name</th><th className="pr-3">Discount</th><th className="pr-3">Applies</th>
                  <th className="pr-3">Window</th><th className="pr-3">Used</th><th className="pr-3">Live</th><th></th>
                </tr></thead>
                <tbody>{promos.map(p => {
                  const live = p.is_active
                    && (!p.starts_at || new Date(p.starts_at) <= new Date())
                    && (!p.ends_at || new Date(p.ends_at) > new Date());
                  return (
                    <tr key={p.id} className="border-b border-grayscale-200">
                      <td className="py-2 pr-3 text-grayscale-1300">
                        {p.name}
                        {p.code && <span className="ml-2 font-mono text-xs text-grayscale-1100">{p.code}</span>}
                        {!p.auto_apply && <span className="ml-2 text-[10px] text-grayscale-1100">(code required)</span>}
                      </td>
                      <td className="pr-3">{p.discount_type === 'percent' ? `${p.discount_value}%` : inr(p.discount_value)}
                        {p.intro_cycles > 0 && <span className="text-xs text-grayscale-1100"> · first {p.intro_cycles}</span>}
                      </td>
                      <td className="pr-3 text-xs">{(p.plan_codes || []).length ? p.plan_codes.join(', ') : 'all plans'}</td>
                      <td className="pr-3 text-xs">{p.starts_at ? fmtDate(p.starts_at) : '—'} → {p.ends_at ? fmtDate(p.ends_at) : 'open'}</td>
                      <td className="pr-3">{p.redeemed_count}{p.max_redemptions != null ? ` / ${p.max_redemptions}` : ''}
                        {((p.eligible_emails || []).length > 0 || p.eligible_signup_before) && (
                          <span title={`${(p.eligible_emails || []).length} allowed email(s)`
                            + (p.eligible_signup_before ? `, signed up before ${p.eligible_signup_before.slice(0, 10)}` : '')}
                            className="ml-1 px-1.5 py-0.5 rounded bg-primary-100 text-primary-og-600 text-[10px]">
                            limited
                          </span>)}
                      </td>
                      <td className="pr-3">{live
                        ? <span className="text-green-700">live</span>
                        : <span className="text-grayscale-1100">{p.is_active ? 'scheduled/ended' : 'off'}</span>}</td>
                      <td className="text-right whitespace-nowrap">
                        <Btn subtle onClick={() => setPromoForm({
                          ...p, plan_codes: p.plan_codes || [],
                          starts_at: p.starts_at ? p.starts_at.slice(0, 10) : '',
                          ends_at: p.ends_at ? p.ends_at.slice(0, 10) : '',
                          max_redemptions: p.max_redemptions ?? '',
                          // Stored as a JSON array, edited as pasted text.
                          eligible_emails: (p.eligible_emails || []).join('\n'),
                          eligible_signup_before: p.eligible_signup_before
                            ? p.eligible_signup_before.slice(0, 10) : '',
                          offer_ids: p.offer_ids || {},
                        })}>Edit</Btn>{' '}
                        <Btn subtle onClick={async () => {
                          const r = await apiService.adminBillingSavePromotion(userId, { ...p, is_active: !p.is_active });
                          if (r.success && r.data?.success) { flash('Updated'); loadPromos(); } else fail(r.error || 'Failed');
                        }}>{p.is_active ? 'Disable' : 'Enable'}</Btn>{' '}
                        <Btn subtle onClick={async () => {
                          setVoucherFor(p);
                          const r = await apiService.adminBillingVouchers(userId, p.id);
                          setVouchers(r.success && r.data?.success ? r.data.vouchers || [] : []);
                        }}>Codes</Btn>{' '}
                        <Btn subtle onClick={async () => {
                          const used = Number(p.redeemed_count || 0);
                          // confirm is passed only when there IS history to
                          // lose, so the server's guard still bites for
                          // anything called from outside this screen.
                          if (!window.confirm(
                            `Delete the promotion "${p.name}" permanently?`
                            + (used ? `\n\nIt has been redeemed ${used} time(s) — `
                                      + 'deleting loses that record.' : '')
                            + '\n\nDisable retires it instead and keeps the history.')) return;
                          const r = await apiService.adminBillingDeletePromotion(
                            userId, p.id, used > 0);
                          if (r.success && r.data?.success) { flash('Promotion deleted'); loadPromos(); }
                          else fail(r.error || 'Delete failed');
                        }}>Delete</Btn>
                      </td>
                    </tr>
                  );
                })}</tbody>
              </table>
              {!promos.length && <div className="text-xs text-grayscale-1100 pt-2">No promotions yet.</div>}
              {voucherFor && (
                <div className="border-t border-grayscale-200 mt-3 pt-3 flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">
                      One-time codes — {voucherFor.name}
                      <span className="ml-2 text-xs text-grayscale-1100">
                        {vouchers.filter((v: any) => !v.redeemed_by).length} unused of {vouchers.length}
                      </span>
                    </div>
                    <Btn subtle onClick={() => { setVoucherFor(null); setVouchers([]); }}>Close</Btn>
                  </div>
                  <div className="text-[11px] text-grayscale-1100">
                    Each code works once, for whoever redeems it first. Nothing is tied
                    to an email address, so it does not matter which one they sign up with.
                  </div>
                  <div className="flex gap-2 items-center flex-wrap">
                    <input type="number" min={1} max={500} value={vCount}
                      onChange={e => setVCount(e.target.value)}
                      className="h-9 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm w-20" />
                    <input placeholder="prefix (optional)" value={vPrefix}
                      onChange={e => setVPrefix(e.target.value)}
                      className="h-9 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm w-40" />
                    <Btn onClick={async () => {
                      const r = await apiService.adminBillingGenerateVouchers(
                        userId, voucherFor.id, Number(vCount) || 0, vPrefix.trim());
                      if (r.success && r.data?.success) {
                        flash(`Generated ${r.data.created} code(s)`);
                        const l = await apiService.adminBillingVouchers(userId, voucherFor.id);
                        setVouchers(l.success && l.data?.success ? l.data.vouchers || [] : []);
                      } else fail(r.error || 'Could not generate codes');
                    }}>Generate</Btn>
                  </div>
                  {vouchers.some((v: any) => !v.redeemed_by) && (
                    <>
                      <div className="text-[11px] text-grayscale-1100">
                        Unused codes — copy and send these out:
                      </div>
                      {/* A plain textarea rather than a copy button: it works
                          without clipboard permissions and lets them grab a
                          subset by hand. */}
                      <textarea readOnly rows={4}
                        value={vouchers.filter((v: any) => !v.redeemed_by)
                          .map((v: any) => v.issued_to ? `${v.code}\t${v.issued_to}` : v.code)
                          .join('\n')}
                        className="w-full px-2.5 py-2 rounded-md border-[0.5px] border-primary-300 text-sm font-mono" />
                    </>
                  )}
                  <table className="w-full text-left text-xs">
                    <thead className="text-grayscale-1100">
                      <tr><th className="py-1 pr-3">Code</th><th className="pr-3">Issued to</th>
                          <th className="pr-3">Status</th><th /></tr>
                    </thead>
                    <tbody>{vouchers.map((v: any) => (
                      <tr key={v.code} className="border-b border-grayscale-200">
                        <td className="py-1.5 pr-3 font-mono">{v.code}</td>
                        <td className="pr-3">{v.issued_to || '—'}</td>
                        <td className="pr-3">{v.redeemed_by
                          ? <span className="text-grayscale-1100">
                              used{v.redeemed_email ? ` · ${v.redeemed_email}` : ''}
                              {v.redeemed_at ? ` · ${fmtDate(v.redeemed_at)}` : ''}
                            </span>
                          : <span className="text-green-700">unused</span>}</td>
                        <td className="text-right">
                          {!v.redeemed_by && (
                            <Btn subtle onClick={async () => {
                              if (!window.confirm(`Revoke ${v.code}? Anyone holding it will `
                                + 'no longer be able to use it.')) return;
                              const r = await apiService.adminBillingDeleteVoucher(userId, v.code);
                              if (r.success && r.data?.success) {
                                setVouchers(vs => vs.filter((x: any) => x.code !== v.code));
                              } else fail(r.error || 'Could not revoke');
                            }}>Revoke</Btn>)}
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                  {!vouchers.length && (
                    <div className="text-xs text-grayscale-1100">
                      No codes yet — generate some above.
                    </div>)}
                </div>
              )}
            </Card>
          </div>
        )}

        {tab === 'prompts' && <UpgradePromptsPanel />}

        {tab === 'emails' && <BillingEmailsPanel />}

        {tab === 'coupons' && (
          <div className="flex flex-col gap-4">
            <Card title="New / update coupon (applies to one-time payments)">
              <div className="flex flex-wrap gap-2 items-center">
                <input placeholder="CODE" value={couponForm.code}
                  onChange={e => setCouponForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                  className="h-9 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm w-36" />
                <select value={couponForm.discount_type}
                  onChange={e => setCouponForm(f => ({ ...f, discount_type: e.target.value }))}
                  className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 text-sm">
                  <option value="percent">% off</option>
                  <option value="flat">₹ off</option>
                </select>
                <input type="number" value={couponForm.discount_value}
                  onChange={e => setCouponForm(f => ({ ...f, discount_value: Number(e.target.value) }))}
                  className="h-9 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm w-24" />
                <input placeholder="Max uses (blank = unlimited)" value={couponForm.max_redemptions}
                  onChange={e => setCouponForm(f => ({ ...f, max_redemptions: e.target.value }))}
                  className="h-9 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm w-52" />
                <input type="date" value={couponForm.valid_until}
                  onChange={e => setCouponForm(f => ({ ...f, valid_until: e.target.value }))}
                  className="h-9 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm" />
                <input placeholder="Description" value={couponForm.description}
                  onChange={e => setCouponForm(f => ({ ...f, description: e.target.value }))}
                  className="h-9 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm flex-1 min-w-40" />
                <Btn onClick={async () => {
                  const payload: any = {
                    code: couponForm.code, description: couponForm.description,
                    discount_type: couponForm.discount_type,
                    // flat coupons are entered in rupees, stored in paise
                    discount_value: couponForm.discount_type === 'flat'
                      ? Math.round(Number(couponForm.discount_value) * 100)
                      : Number(couponForm.discount_value),
                    is_active: true,
                  };
                  if (couponForm.max_redemptions.trim()) payload.max_redemptions = Number(couponForm.max_redemptions);
                  if (couponForm.valid_until) payload.valid_until = new Date(couponForm.valid_until + 'T23:59:59').toISOString();
                  const r = await apiService.adminBillingSaveCoupon(userId, payload);
                  if (r.success && r.data?.success) { flash(`Coupon ${payload.code} saved`); loadCoupons(); }
                  else fail(r.error || 'Save failed');
                }}>Save coupon</Btn>
              </div>
            </Card>
            <Card title="Coupons">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-grayscale-1100 border-b border-grayscale-300">
                  <th className="py-2 pr-3">Code</th><th className="pr-3">Discount</th><th className="pr-3">Used</th>
                  <th className="pr-3">Valid until</th><th className="pr-3">Active</th><th></th>
                </tr></thead>
                <tbody>{coupons.map(c => (
                  <tr key={c.code} className="border-b border-grayscale-200">
                    <td className="py-2 pr-3 font-mono">{c.code}</td>
                    <td className="pr-3">{c.discount_type === 'percent' ? `${c.discount_value}%` : inr(c.discount_value)}</td>
                    <td className="pr-3">{c.redeemed_count}{c.max_redemptions != null ? ` / ${c.max_redemptions}` : ''}</td>
                    <td className="pr-3">{fmtDate(c.valid_until)}</td>
                    <td className="pr-3">{c.is_active ? 'yes' : 'no'}</td>
                    <td className="text-right">
                      <Btn subtle onClick={async () => {
                        const r = await apiService.adminBillingSaveCoupon(userId, { ...c, is_active: !c.is_active });
                        if (r.success && r.data?.success) { flash('Updated'); loadCoupons(); } else fail(r.error || 'Failed');
                      }}>{c.is_active ? 'Deactivate' : 'Activate'}</Btn>{' '}
                      <Btn subtle onClick={async () => {
                        const used = Number(c.redeemed_count || 0);
                        if (!window.confirm(
                          `Delete the coupon "${c.code}" permanently?`
                          + (used ? `\n\nIt has been redeemed ${used} time(s) — `
                                    + 'deleting loses that record.' : '')
                          + '\n\nDeactivate retires it instead and keeps the history.')) return;
                        const r = await apiService.adminBillingDeleteCoupon(
                          userId, c.code, used > 0);
                        if (r.success && r.data?.success) { flash('Coupon deleted'); loadCoupons(); }
                        else fail(r.error || 'Delete failed');
                      }}>Delete</Btn>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </Card>
          </div>
        )}

        {tab === 'razorpay' && (() => {
          const cur = gw[gwSub];
          const mode: string = cur?.mode || gateway?.mode || '';
          const rows = gwRows(gwSub);
          const cell = 'py-2 pr-3 align-top';
          const mono = 'font-mono text-[11px]';
          return (
            <div className="flex flex-col gap-4">
              {/* Header: which environment these rows come from, when they
                  were read, and the three lists. The mode badge is the same
                  red/amber as the Gateway tab's because a LIVE list is one
                  where every button below moves real money. */}
              <Card>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      mode === 'live'
                        ? 'bg-red-50 text-red-700 border border-red-300'
                        : 'bg-amber-50 text-amber-700 border border-amber-300'}`}
                      title="The Razorpay environment these rows come from. Switch it on the Gateway tab.">
                      {(mode || '…').toUpperCase()}
                    </span>
                    <div className="flex border-b border-grayscale-300">
                      {(['subscriptions', 'payments', 'refunds'] as GwKind[]).map(k => (
                        <button key={k} onClick={() => setGwSub(k)}
                          className={`py-1.5 px-3 text-sm font-grotesk capitalize ${gwSub === k
                            ? 'border-b-2 border-primary-700 text-primary-700 font-bold'
                            : 'text-grayscale-1200'}`}>
                          {k}{gw[k]?.count != null ? ` (${gw[k].count})` : ''}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-xs text-grayscale-1100">
                    {cur?.fetched_at && (
                      <span title={cur.cached ? 'Served from the server\'s 60 s cache' : 'Read from Razorpay just now'}>
                        read {fmtWhen(cur.fetched_at)}{cur.cached ? ' (cached)' : ''}
                      </span>
                    )}
                    <Btn subtle disabled={gwBusy} onClick={() => loadGw(gwSub, true)}>
                      {gwBusy ? 'Reading…' : 'Refresh'}
                    </Btn>
                    <Btn subtle onClick={() => setTab('gateway')}>Switch mode…</Btn>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap mt-3">
                  {gwSub === 'subscriptions' && (
                    <select value={gwStatus} onChange={e => setGwStatus(e.target.value)}
                      className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm">
                      {GW_SUB_STATUSES.map(st => <option key={st} value={st}>{st}</option>)}
                    </select>
                  )}
                  <input value={gwQ} onChange={e => setGwQ(e.target.value)}
                    placeholder="Filter by e-mail, id, plan…"
                    className="h-9 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm w-72" />
                  {gwQ && <Btn subtle onClick={() => setGwQ('')}>Clear</Btn>}
                  {cur?.has_more && (
                    <span className="text-xs text-amber-700">
                      Razorpay has more than the first 100 — narrow the status or filter.
                    </span>
                  )}
                </div>
                {cur?.error && (
                  <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    Razorpay: {cur.error}
                  </div>
                )}
              </Card>

              {/* ---- Subscriptions: the dashboard's list, plus Our account ---- */}
              {gwSub === 'subscriptions' && (
                <Card title={`Subscriptions at Razorpay — ${gwStatus}`}>
                  <p className="text-xs text-grayscale-1100 mb-3 max-w-3xl">
                    Every mandate Razorpay holds in this environment. <b>Our account</b> is
                    who it belongs to on our side; red means nobody — a deleted account, or a
                    subscription we never recorded — and that mandate will keep charging until
                    it is cancelled here. Cancel stops it <i>now</i>, not at cycle end.
                  </p>
                  {cur === null ? (
                    <div className="text-sm text-grayscale-1100">Reading from Razorpay…</div>
                  ) : !rows.length ? (
                    <div className="text-sm text-grayscale-1100">
                      {cur.error ? 'Nothing to show.' : `No ${gwStatus === 'all' ? '' : gwStatus + ' '}subscriptions${gwQ ? ' match the filter' : ''}.`}
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-xs text-grayscale-1100 border-b border-grayscale-300">
                          <th className="py-2 pr-3">Subscription</th><th className="pr-3">Plan</th>
                          <th className="pr-3">Customer</th><th className="pr-3">Next due</th>
                          <th className="pr-3">Created</th><th className="pr-3">Status</th>
                          <th className="pr-3">Charges</th><th className="pr-3">Our account</th><th></th>
                        </tr></thead>
                        <tbody>{rows.map((r: any) => {
                          const live = GW_LIVE.has(String(r.status || ''));
                          const lp = r.latest_payment;
                          const payer = r.customer?.email || r.notes?.email || r.our_row?.email || r.customer_id || r.id;
                          return (
                            <tr key={r.id} className="border-b border-grayscale-200">
                              <td className={cell}>
                                <div className={mono}>{r.id}</div>
                                {r.short_url && (
                                  <a href={r.short_url} target="_blank" rel="noreferrer"
                                    className="text-[10px] underline text-grayscale-1100">payment link</a>
                                )}
                                {r.payment_method && <div className="text-[10px] text-grayscale-1100">{r.payment_method}</div>}
                              </td>
                              <td className={cell}>
                                <div>{r.plan?.name || r.plan_id || '—'}</div>
                                <div className="text-[10px] text-grayscale-1100">
                                  {[r.plan?.code, r.plan?.period,
                                    r.plan?.amount != null ? money(r.plan.amount, r.plan.currency) : null,
                                    r.plan && !r.plan.local ? 'not one of ours' : null]
                                    .filter(Boolean).join(' · ') || r.plan_id}
                                </div>
                              </td>
                              <td className={cell}>
                                <div>{r.customer?.email || r.notes?.email || <span className="text-grayscale-1100">unknown</span>}</div>
                                <div className="text-[10px] text-grayscale-1100">
                                  {[r.customer?.contact, r.customer?.name].filter(Boolean).join(' · ')}
                                </div>
                                {r.customer_id && <div className={`${mono} text-grayscale-1100`}>{r.customer_id}</div>}
                              </td>
                              <td className={`${cell} whitespace-nowrap`}>
                                {live && r.charge_at
                                  ? <span className={new Date(r.charge_at).getTime() - Date.now() < 3 * 864e5 ? 'text-red-600 font-medium' : ''}>
                                      {fmtDate(r.charge_at)}
                                    </span>
                                  : <span className="text-grayscale-1100">—</span>}
                                {r.current_end && <div className="text-[10px] text-grayscale-1100">period ends {fmtDate(r.current_end)}</div>}
                              </td>
                              <td className={`${cell} whitespace-nowrap`}>{fmtDate(r.created_at)}</td>
                              <td className={cell}>
                                <span className={`px-2 py-0.5 rounded-full text-[11px] border ${gwTone(r.status)}`}>{r.status}</span>
                                {r.ended_at && <div className="text-[10px] text-grayscale-1100">ended {fmtDate(r.ended_at)}</div>}
                              </td>
                              <td className={`${cell} whitespace-nowrap`}>
                                {r.paid_count ?? 0}{r.total_count ? ` / ${r.total_count}` : ''}
                                {lp && (
                                  <div className="text-[10px] text-grayscale-1100" title={lp.id}>
                                    last {money(lp.amount, lp.currency)} · {fmtDate(lp.created_at)}
                                    {lp.status && lp.status !== 'captured' ? ` · ${lp.status}` : ''}
                                    {!lp.local ? ' · not in our DB' : ''}
                                  </div>
                                )}
                              </td>
                              <td className={cell}>{ourAccountCell(r.our_row, r.notes_user)}</td>
                              <td className={`${cell} text-right whitespace-nowrap`}>
                                <div className="flex flex-col items-end gap-1">
                                  {live && (
                                    <button onClick={() => setGwAction({ kind: 'cancel', sub: r, reason: '', busy: false })}
                                      className="py-1 px-2.5 rounded-full text-xs font-medium border bg-red-50 text-red-700 border-red-200 hover:bg-red-100">
                                      Cancel at gateway
                                    </button>
                                  )}
                                  {lp?.refundable && lp.id && (
                                    <Btn subtle onClick={() => openGwRefund({
                                      id: lp.id, amount: lp.amount, currency: lp.currency,
                                      payer, ours: r.our_row && !r.our_row.user_missing ? r.our_row.email : null,
                                      subscription_id: r.id })}>
                                      Refund latest charge
                                    </Btn>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}</tbody>
                      </table>
                    </div>
                  )}
                </Card>
              )}

              {/* ---- Payments: Razorpay's list, refundable from here ---- */}
              {gwSub === 'payments' && (
                <Card title="Payments at Razorpay (newest 100)">
                  <p className="text-xs text-grayscale-1100 mb-3 max-w-3xl">
                    As Razorpay lists them. A payment with no account on our side can still be
                    refunded from here; a full refund also stops the mandate it was charged on.
                  </p>
                  {cur === null ? (
                    <div className="text-sm text-grayscale-1100">Reading from Razorpay…</div>
                  ) : !rows.length ? (
                    <div className="text-sm text-grayscale-1100">{cur.error ? 'Nothing to show.' : 'No payments.'}</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-xs text-grayscale-1100 border-b border-grayscale-300">
                          <th className="py-2 pr-3">Date</th><th className="pr-3">Payment</th>
                          <th className="pr-3">Payer</th><th className="pr-3">Amount</th>
                          <th className="pr-3">Method</th><th className="pr-3">Status</th>
                          <th className="pr-3">Refund</th><th className="pr-3">Our account</th><th></th>
                        </tr></thead>
                        <tbody>{rows.map((p: any) => {
                          const refunded = Number(p.amount_refunded || 0);
                          const canRefund = p.status === 'captured' && refunded < Number(p.amount || 0);
                          const ours = p.our_row && !p.our_row.user_missing ? p.our_row.email : null;
                          return (
                            <tr key={p.id} className="border-b border-grayscale-200">
                              <td className={`${cell} whitespace-nowrap`}>{fmtWhen(p.created_at)}</td>
                              <td className={cell}>
                                <div className={mono}>{p.id}</div>
                                {p.description && <div className="text-[10px] text-grayscale-1100 max-w-[14rem] truncate" title={p.description}>{p.description}</div>}
                                {p.order_id && <div className={`${mono} text-grayscale-1100`}>{p.order_id}</div>}
                              </td>
                              <td className={cell}>
                                <div>{p.email || <span className="text-grayscale-1100">—</span>}</div>
                                <div className="text-[10px] text-grayscale-1100">{p.contact || ''}</div>
                              </td>
                              <td className={`${cell} whitespace-nowrap`}>
                                {money(p.amount, p.currency)}
                                {p.international && <span className="text-[10px] text-grayscale-1100"> intl</span>}
                              </td>
                              <td className={cell}>{p.method || '—'}</td>
                              <td className={cell}>
                                <span className={`px-2 py-0.5 rounded-full text-[11px] border ${gwTone(p.status)}`}>{p.status}</span>
                                {p.cause && <div className="text-[11px] text-grayscale-1300 max-w-[16rem]">{p.cause}</div>}
                                {p.error_description && <div className="text-[10px] text-red-600 max-w-[12rem]">{p.error_description}</div>}
                              </td>
                              <td className={`${cell} whitespace-nowrap`}>
                                {p.refund_status
                                  ? <span className="text-amber-700">{p.refund_status}{refunded ? ` · ${money(refunded, p.currency)}` : ''}</span>
                                  : <span className="text-grayscale-1100">—</span>}
                              </td>
                              <td className={cell}>
                                {p.our_row ? ourAccountCell(p.our_row)
                                  : <span className="text-red-600 font-medium">not in our DB</span>}
                                {p.our_row?.status && p.our_row.status !== p.status && (
                                  <div className="text-[10px] text-grayscale-1100">ours says {p.our_row.status}</div>
                                )}
                              </td>
                              <td className={`${cell} text-right whitespace-nowrap`}>
                                {canRefund && (
                                  <Btn subtle onClick={() => openGwRefund({
                                    id: p.id, amount: p.amount, currency: p.currency,
                                    amount_refunded: refunded, payer: p.email || p.contact || p.id, ours })}>
                                    Refund
                                  </Btn>
                                )}
                              </td>
                            </tr>
                          );
                        })}</tbody>
                      </table>
                    </div>
                  )}
                </Card>
              )}

              {/* ---- Refunds: Razorpay's list against our log ---- */}
              {gwSub === 'refunds' && (
                <Card title="Refunds at Razorpay (newest 100)">
                  <p className="text-xs text-grayscale-1100 mb-3 max-w-3xl">
                    A refund with nothing in <b>Our log</b> was made from the Razorpay dashboard
                    directly — nothing on our side ended the access it paid for.
                  </p>
                  {cur === null ? (
                    <div className="text-sm text-grayscale-1100">Reading from Razorpay…</div>
                  ) : !rows.length ? (
                    <div className="text-sm text-grayscale-1100">{cur.error ? 'Nothing to show.' : 'No refunds.'}</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-xs text-grayscale-1100 border-b border-grayscale-300">
                          <th className="py-2 pr-3">Date</th><th className="pr-3">Refund</th>
                          <th className="pr-3">Payment</th><th className="pr-3">Amount</th>
                          <th className="pr-3">Status</th><th className="pr-3">Our log</th>
                        </tr></thead>
                        <tbody>{rows.map((r: any) => (
                          <tr key={r.id} className="border-b border-grayscale-200">
                            <td className={`${cell} whitespace-nowrap`}>{fmtWhen(r.created_at)}</td>
                            <td className={cell}><span className={mono}>{r.id}</span></td>
                            <td className={cell}><span className={mono}>{r.payment_id}</span></td>
                            <td className={`${cell} whitespace-nowrap`}>{money(r.amount, r.currency)}</td>
                            <td className={cell}>
                              <span className={`px-2 py-0.5 rounded-full text-[11px] border ${gwTone(r.status)}`}>{r.status}</span>
                              {r.speed && <div className="text-[10px] text-grayscale-1100">{r.speed}</div>}
                            </td>
                            <td className={cell}>
                              {r.our_row ? (
                                <>
                                  <div>
                                    {r.our_row.email
                                      || <span className="text-grayscale-1100">no account (gateway refund)</span>}
                                    {r.our_row.full_refund ? ' · full' : ' · partial'}
                                    {r.our_row.end_access ? ' · access ended' : ''}
                                  </div>
                                  <div className="text-[10px] text-grayscale-1100">
                                    {r.our_row.reason || '(no reason)'} — {r.our_row.refunded_by || '—'}
                                  </div>
                                </>
                              ) : (
                                <span className="text-amber-700">not in our log — made at Razorpay</span>
                              )}
                              {r.notes?.reason && !r.our_row && (
                                <div className="text-[10px] text-grayscale-1100">notes: {r.notes.reason}{r.notes.by ? ` — ${r.notes.by}` : ''}</div>
                              )}
                            </td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  )}
                </Card>
              )}
            </div>
          );
        })()}

        {tab === 'gateway' && (
          <div className="flex flex-col gap-4">
            {/* Which Razorpay environment the whole billing flow talks to. */}
            <div className="rounded-xl border border-grayscale-300 bg-white p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-sm font-bold text-grayscale-1300 font-grotesk">Razorpay environment</div>
                  <div className="text-xs text-grayscale-1100">
                    Applies to every checkout, subscription and refund. Defaults to test.
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  gateway?.mode === 'live'
                    ? 'bg-red-50 text-red-700 border border-red-300'
                    : 'bg-amber-50 text-amber-700 border border-amber-300'}`}>
                  {(gateway?.mode || '…').toUpperCase()}
                </span>
              </div>

              {gateway?.warning && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {gateway.warning}
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-3">
                {['test', 'live'].map(m => {
                  const e = gateway?.environments?.[m];
                  const isActive = gateway?.mode === m;
                  return (
                    <div key={m} className={`rounded-lg border p-3 flex flex-col gap-1.5 ${
                      isActive ? 'border-primary-500 bg-primary-100/30' : 'border-grayscale-300'}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold text-grayscale-1300">{m.toUpperCase()}</span>
                        {isActive && <span className="text-[10px] text-primary-700 font-bold">ACTIVE</span>}
                      </div>
                      <div className="text-xs text-grayscale-1200">
                        Key id: {e?.key_id_prefix ? <code>{e.key_id_prefix}…</code> : <span className="text-red-600">not set</span>}
                      </div>
                      <div className="text-xs text-grayscale-1200">
                        Secret: {e?.key_secret_present ? 'set' : <span className="text-red-600">not set</span>}
                      </div>
                      <div className="text-xs text-grayscale-1200">
                        Webhook secret: {e?.webhook_secret_present
                          ? 'set'
                          : <span className="text-amber-700">not set — webhook deliveries will be rejected</span>}
                      </div>
                      {e?.mismatch && <div className="text-xs text-red-600">{e.mismatch}</div>}
                      {!isActive && (
                        <button
                          disabled={!e?.ready || switching}
                          onClick={() => switchMode(m as 'test' | 'live')}
                          className={`mt-1 py-1.5 px-3 rounded-full text-xs font-medium ${
                            e?.ready
                              ? (m === 'live'
                                  ? 'bg-red-600 text-white'
                                  : 'bg-primary-500 text-white')
                              : 'bg-grayscale-200 text-grayscale-1100 cursor-not-allowed'}`}>
                          {switching ? 'Switching…' : `Switch to ${m}`}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <p className="text-xs text-grayscale-1100">
                Keys are read from the server environment and never sent to the browser —
                only whether each one is present, and its <code>rzp_test_</code> /
                <code>rzp_live_</code> prefix, which is what proves the key matches the mode
                it is filed under. Switching is refused if they disagree.
              </p>
            </div>

            {/* Concierge routing */}
            <div className="rounded-xl border border-grayscale-300 bg-white p-4 flex flex-col gap-2">
              <div className="text-sm font-bold text-grayscale-1300 font-grotesk">
                Concierge request recipients
              </div>
              <p className="text-xs text-grayscale-1100">
                Every "ask our concierge" request is emailed to
                <b> {conciergeInbox || 'info@universitytech.io'}</b>. Add anyone else who
                should be copied, comma separated. The requester is never copied — a human
                replies to them separately.
              </p>
              <div className="flex gap-2 items-center flex-wrap">
                <input value={conciergeCc} onChange={e => setConciergeCc(e.target.value)}
                  placeholder="someone@universitytech.io, another@…"
                  className="h-9 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm flex-1 min-w-[18rem]" />
                <Btn onClick={async () => {
                  const emails = conciergeCc.split(',').map(s => s.trim()).filter(Boolean);
                  const r = await apiService.adminSetConciergeCc(userId, emails);
                  if (r.success && r.data?.success) {
                    flash(`Copying ${r.data.emails.length} address(es)`);
                    setConciergeCc((r.data.emails || []).join(', '));
                  } else fail(r.error || 'Could not save');
                }}>Save</Btn>
              </div>
            </div>

            {/* Danger zone */}
            <div className="rounded-xl border border-red-300 bg-red-50/40 p-4 flex flex-col gap-2">
              <div className="text-sm font-bold text-red-700 font-grotesk">Reset all billing</div>
              <p className="text-xs text-grayscale-1200">
                Deletes every account's subscriptions and payment records, and cancels their
                live Razorpay subscriptions so no card is charged again. Intended for clearing
                test data before going live. <b>This cannot be undone.</b>
              </p>
              <div className="flex gap-2 items-center flex-wrap">
                <input value={resetPhrase} onChange={e => setResetPhrase(e.target.value)}
                  placeholder="Type: RESET ALL BILLING"
                  className="h-9 px-2.5 rounded-md border-[0.5px] border-red-300 text-sm w-64" />
                <button
                  disabled={resetPhrase !== 'RESET ALL BILLING' || switching}
                  onClick={async () => {
                    if (!window.confirm('Delete billing history for EVERY account?')) return;
                    setSwitching(true); setErr('');
                    const r = await apiService.adminResetAllBilling(
                      apiService.getStoredUserId() || '', resetPhrase);
                    setSwitching(false);
                    if (r.success && r.data?.success) {
                      const g = r.data.gateway || {};
                      setMsg(`Reset ${r.data.users_affected} account(s): `
                        + `${r.data.subscriptions_deleted} subscription(s), `
                        + `${r.data.payments_deleted} payment(s)`
                        + (g.cancelled?.length ? `; ${g.cancelled.length} cancelled at Razorpay` : ''));
                      setResetPhrase('');
                    } else setErr(r.error || 'Reset failed');
                  }}
                  className={`py-1.5 px-3 rounded-full text-xs font-medium ${
                    resetPhrase === 'RESET ALL BILLING'
                      ? 'bg-red-600 text-white' : 'bg-grayscale-200 text-grayscale-1100 cursor-not-allowed'}`}>
                  Reset all billing
                </button>
              </div>
            </div>

            {/* Webhook deliveries */}
            <div className="rounded-xl border border-grayscale-300 bg-white p-4 flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-bold text-grayscale-1300 font-grotesk">Webhook deliveries</div>
                <button onClick={loadGateway}
                  className="py-1.5 px-3 rounded-full text-xs border border-grayscale-400 text-grayscale-1200">
                  Refresh
                </button>
              </div>
              <p className="text-xs text-grayscale-1100">
                Point Razorpay at <code>{gateway?.webhook_url_path || '/api/v1/webhooks/razorpay'}</code>.
                Test and live have separate webhook settings and separate secrets, so both
                need configuring. Without this, a customer who closes the tab after paying —
                or any background auto-renew — is charged without being granted access.
              </p>
              {hooks.length === 0 ? (
                <div className="text-sm text-grayscale-1100 py-3">
                  No webhook deliveries recorded yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-grayscale-200 text-left">
                        {['When', 'Event', 'Mode', 'Payment', 'User', 'Result'].map(h => (
                          <th key={h} className="p-2 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {hooks.map((h, i) => (
                        <tr key={i} className="border-t border-grayscale-200">
                          <td className="p-2 whitespace-nowrap">{String(h.received_at || '').slice(0, 19)}</td>
                          <td className="p-2 whitespace-nowrap">{h.event}</td>
                          <td className="p-2">{h.mode}</td>
                          <td className="p-2 whitespace-nowrap">{h.payment_id || '—'}</td>
                          <td className="p-2 whitespace-nowrap">{h.user_id || '—'}</td>
                          <td className={`p-2 ${h.handled ? 'text-green-700' : 'text-grayscale-1100'}`}>
                            {h.outcome || (h.handled ? 'handled' : '—')}
                            {h.cause && <div className="text-[11px] text-grayscale-1300 mt-0.5" title={h.cause_detail || undefined}>{h.cause}</div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'payments' && (
          <div className="flex flex-col gap-4">
          {/* A payment made outside the checkout (a Razorpay link, a bank
              transfer) tied to its account, which becomes a paid member. */}
          <ManualPaymentCard onRecorded={() => loadPayments()} />
          {/* Every failed checkout and payment with its cause in words — the
              same "What happened" line the team alert e-mail carries. */}
          <FailedAttemptsCard />
          {/* Money on a user id that no longer exists. The 2026-09-20 case:
              an autopay trial's account was deleted and re-registered under
              a new id, the old mandate charged ₹9,000 three days later, and
              the panel showed a bare uuid with no way to say who paid. The
              webhook payload always carried the payer's e-mail and phone, so
              the server groups the ghost rows by id with that identity and
              proposes the account that now holds the e-mail. Sits ABOVE the
              payments table because it is the row that needs a human. */}
          <Card title={`Orphaned payments${orphans ? ` (${orphans.length})` : ''}`}>
            {orphansErr ? (
              <div className="text-xs text-red-600">Could not load orphaned payments: {orphansErr}</div>
            ) : orphans === null ? (
              <div className="text-xs text-grayscale-1100">Loading…</div>
            ) : !orphans.length ? (
              <div className="text-xs text-grayscale-1100">No orphaned payments.</div>
            ) : (
              <>
                <div className="text-xs text-grayscale-1100 mb-3 max-w-3xl">
                  Payments, subscriptions and webhooks filed under an account that has
                  since been deleted. A live mandate here will charge again — re-attribute
                  the rows to the account the payer uses now (their history and any
                  refund then land on a real user), or refund from the table below.
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-grayscale-1100 border-b border-grayscale-300">
                      <th className="py-2 pr-3">Payer</th><th className="pr-3">Contact</th>
                      <th className="pr-3">Payments</th><th className="pr-3">Subscriptions</th>
                      <th className="pr-3">Live mandates</th><th className="pr-3">Last activity</th>
                      <th className="pr-3">Suggested account</th><th></th>
                    </tr></thead>
                    <tbody>{orphans.map((o: any) => {
                      const sug = o.suggested_user;
                      const typed = (orphanTarget[o.user_id] || '').trim();
                      const busy = orphanBusy === o.user_id;
                      return (
                        <tr key={o.user_id} className="border-b border-grayscale-200 align-top">
                          <td className="py-2 pr-3">
                            <div className="text-grayscale-1300">{o.payer_email || <span className="text-grayscale-1100">unknown payer</span>}</div>
                            <div className="font-mono text-[10px] text-grayscale-1100" title="The deleted account's id">
                              {o.user_id}
                            </div>
                          </td>
                          <td className="pr-3 whitespace-nowrap">{o.payer_contact || '—'}</td>
                          <td className="pr-3 whitespace-nowrap">
                            {o.payments ?? 0}
                            <span className="text-grayscale-1100"> · {amountsByCurrency(o.amount_by_currency)}</span>
                          </td>
                          <td className="pr-3">{o.subscriptions ?? 0}</td>
                          {/* Red is the point of the card: a mandate still
                              live at Razorpay on an account nobody can see. */}
                          <td className="pr-3">
                            {(o.live_mandates ?? 0) > 0
                              ? <span className="text-red-600 font-medium">{o.live_mandates} — will charge again</span>
                              : <span className="text-grayscale-1100">0</span>}
                          </td>
                          <td className="pr-3 whitespace-nowrap">{fmtWhen(o.last_at)}</td>
                          <td className="pr-3">
                            {sug ? (
                              <>
                                <div className="text-grayscale-1300">{sug.email}</div>
                                {sug.name && <div className="text-xs text-grayscale-1100">{sug.name}</div>}
                              </>
                            ) : (
                              // No account holds the payer's e-mail, so the
                              // admin names one — a user id from the Users tab.
                              <input value={orphanTarget[o.user_id] || ''}
                                onChange={e => setOrphanTarget(t => ({ ...t, [o.user_id]: e.target.value }))}
                                placeholder="target user id…"
                                className="h-8 px-2 w-64 rounded-md border-[0.5px] border-primary-300 bg-white text-xs font-mono" />
                            )}
                          </td>
                          <td className="text-right whitespace-nowrap">
                            {sug ? (
                              <DangerBtn disabled={busy}
                                onConfirm={() => reattribute(o.user_id, sug.id, sug.email)}>
                                {busy ? 'Moving…' : `Re-attribute to ${sug.email}`}
                              </DangerBtn>
                            ) : (
                              <DangerBtn disabled={busy || !typed}
                                title={typed ? '' : 'Enter the user id to move these rows to'}
                                onConfirm={() => reattribute(o.user_id, typed, typed)}>
                                {busy ? 'Moving…' : 'Re-attribute'}
                              </DangerBtn>
                            )}
                          </td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </div>
              </>
            )}
          </Card>

          <Card title="Payments (signature-verified and rejected attempts)">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-grayscale-1100 border-b border-grayscale-300">
                  <th className="py-2 pr-3">Date</th><th className="pr-3">User</th><th className="pr-3">Description</th>
                  <th className="pr-3">Amount</th><th className="pr-3">Status</th><th className="pr-3">Signature</th><th className="pr-3">Coupon</th><th></th>
                </tr></thead>
                <tbody>{payments.map(p => (
                  <tr key={p.id} className="border-b border-grayscale-200">
                    <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(p.created_at)}</td>
                    {/* No users row → the identity Razorpay sent with the
                        charge, flagged so it is not mistaken for a member. A
                        bare uuid is the last resort, for rows from before
                        payer_email was stored. */}
                    <td className="pr-3" title={p.user_id}>
                      {p.email || (p.payer_email
                        ? <>{p.payer_email} <span className="text-red-600 text-xs">(account deleted)</span></>
                        : p.user_id)}
                    </td>
                    <td className="pr-3">{p.description}</td>
                    <td className="pr-3">{money(p.amount_paise, p.currency)}</td>
                    <td className="pr-3">{p.status}</td>
                    <td className="pr-3">{p.signature_valid
                      ? <span className="text-green-700">valid</span>
                      : <span className="text-red-600">REJECTED</span>}</td>
                    <td className="pr-3">{p.coupon_code || '—'}</td>
                    <td className="text-right whitespace-nowrap">
                      {p.refundable && (
                        <Btn subtle onClick={() => setRefund({
                          payment_id: p.razorpay_payment_id,
                          // Same fallback as the User cell: the admin confirms
                          // a refund to a person, not to a deleted account's uuid.
                          email: p.email || (p.payer_email ? `${p.payer_email} (account deleted)` : p.user_id),
                          amount_minor: p.amount_paise,
                          max_minor: p.amount_paise,
                          currency: p.currency || 'INR',
                          reason: '', full: true, end_access: true,
                        })}>Refund</Btn>
                      )}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </Card>
          </div>
        )}

        {/* Promotion editor */}
        {promoForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            {/* Wider than the other dialogs and explicitly overflow-x-hidden:
                this form has the most fields, and at max-w-lg the offer rows
                pushed a horizontal scrollbar and clipped the inputs. */}
            <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full p-6 flex flex-col gap-3 max-h-[90vh] overflow-y-auto overflow-x-hidden">
              <h3 className="text-lg font-bold text-grayscale-1300 font-grotesk">
                {promoForm.id ? 'Edit promotion' : 'New promotion'}
              </h3>
              <input placeholder="Name shown to customers (e.g. Launch week — 10% off)"
                value={promoForm.name}
                onChange={e => setPromoForm((f: any) => ({ ...f, name: e.target.value }))}
                className="h-10 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm" />
              <input placeholder="Description (optional)" value={promoForm.description}
                onChange={e => setPromoForm((f: any) => ({ ...f, description: e.target.value }))}
                className="h-10 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm" />
              <div className="flex gap-2 items-center">
                <select value={promoForm.discount_type}
                  onChange={e => setPromoForm((f: any) => ({ ...f, discount_type: e.target.value }))}
                  className="h-10 px-2 rounded-md border-[0.5px] border-primary-300 text-sm">
                  <option value="percent">% off</option><option value="flat">₹ off (paise)</option>
                </select>
                <input type="number" value={promoForm.discount_value}
                  onChange={e => setPromoForm((f: any) => ({ ...f, discount_value: Number(e.target.value) }))}
                  className="h-10 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm w-28" />
                <label className="text-xs flex items-center gap-1">
                  <input type="checkbox" checked={promoForm.auto_apply}
                    onChange={e => setPromoForm((f: any) => ({ ...f, auto_apply: e.target.checked }))} />
                  auto-apply (no code)
                </label>
                {!promoForm.auto_apply && (
                  <input placeholder="CODE" value={promoForm.code || ''}
                    onChange={e => setPromoForm((f: any) => ({ ...f, code: e.target.value.toUpperCase() }))}
                    className="h-10 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm w-28" />
                )}
              </div>
              <div className="flex gap-2 items-center text-xs text-grayscale-1100">
                Runs
                <input type="date" value={promoForm.starts_at}
                  onChange={e => setPromoForm((f: any) => ({ ...f, starts_at: e.target.value }))}
                  className="h-10 px-2 rounded-md border-[0.5px] border-primary-300 text-sm" />
                to
                <input type="date" value={promoForm.ends_at}
                  onChange={e => setPromoForm((f: any) => ({ ...f, ends_at: e.target.value }))}
                  className="h-10 px-2 rounded-md border-[0.5px] border-primary-300 text-sm" />
              </div>
              <div className="flex gap-2 items-center">
                <span className="text-xs text-grayscale-1100">Plans:</span>
                {(plans.length ? plans : []).map((p: any) => (
                  <label key={p.code} className="text-xs flex items-center gap-1">
                    <input type="checkbox"
                      checked={(promoForm.plan_codes || []).includes(p.code)}
                      onChange={e => setPromoForm((f: any) => ({
                        ...f, plan_codes: e.target.checked
                          ? [...(f.plan_codes || []), p.code]
                          : (f.plan_codes || []).filter((c: string) => c !== p.code),
                      }))} />
                    {p.code}
                  </label>
                ))}
                <span className="text-[11px] text-grayscale-1100">(none = all plans)</span>
              </div>
              <div className="border-t border-grayscale-200 pt-3 flex flex-col gap-2 min-w-0">
                <div className="text-xs font-medium text-grayscale-1300">
                  Razorpay offer — required to discount an auto-renewing plan
                </div>
                <div className="text-[11px] text-grayscale-1100">
                  Paste the offer id. It must be created in Razorpay as
                  <span className="font-medium"> Offers on Subscriptions</span>.
                </div>
                {/* The long explanation is behind a toggle so the form stays
                    readable; it was a wall of text above the fields before. */}
                <details className="text-[11px] text-grayscale-1100">
                  <summary className="cursor-pointer text-primary-700">Why is there no list to pick from?</summary>
                  <div className="pt-1 leading-relaxed">
                    Razorpay&apos;s offers API returns Payments offers only — a Subscriptions
                    offer never appears in it, and there is no way to look one up by id. So a
                    dropdown could only ever show the wrong kind, and those are refused on
                    save. In Razorpay: <span className="font-medium">+ Create New Offer →
                    Offers on Subscriptions</span>, and set <span className="font-medium">Redemption
                    Type = Single Use</span> for a first-cycle discount. With no offer at all this
                    promotion cannot discount a subscription — a recurring charge takes its
                    price from the gateway-side plan.
                  </div>
                </details>
                <div className="text-[11px] text-grayscale-1100">
                  If Razorpay refuses an offer id at checkout, the customer is stopped and told
                  nothing was charged (never signed up at full price), and a red alert names the
                  promotion until the id is changed here or a checkout with it succeeds.
                </div>

                <div className="flex gap-2 items-center flex-wrap min-w-0">
                  <label className="text-xs text-grayscale-1200 w-24 shrink-0">Intro cycles</label>
                  <input type="number" min={0} placeholder="1" value={promoForm.intro_cycles}
                    onChange={e => setPromoForm((f: any) => ({ ...f, intro_cycles: Number(e.target.value) }))}
                    className="h-9 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm w-20" />
                  <span className="text-[11px] text-grayscale-1100">1 = first year only</span>
                </div>

                {[{ key: '', label: 'Any method' },
                  { key: 'card', label: 'Card' },
                  { key: 'upi', label: 'UPI' }].map(row => {
                  const val = row.key
                    ? ((promoForm.offer_ids || {})[row.key] || '')
                    : (promoForm.razorpay_offer_id || '');
                  const set = (v: string) => setPromoForm((f: any) => row.key
                    ? { ...f, offer_ids: { ...(f.offer_ids || {}), [row.key]: v } }
                    : { ...f, razorpay_offer_id: v });
                  // Checked against the offers Razorpay DOES list: anything in
                  // that list is a Payments offer and is refused on save, so
                  // say so now rather than after they hit Save.
                  const wrongKind = !!val.trim() && rzOffers.some((o: any) => o.id === val.trim());
                  return (
                    <div key={row.key || 'any'} className="flex flex-col gap-1 min-w-0">
                      <div className="flex gap-2 items-center min-w-0">
                        <label className="text-xs text-grayscale-1200 w-24 shrink-0">{row.label}</label>
                        <input placeholder="offer_XXXXXXXXXXXX" value={val}
                          onChange={e => set(e.target.value)}
                          className={`h-9 px-2.5 rounded-md border-[0.5px] text-sm flex-1 min-w-0 font-mono ${
                            wrongKind ? 'border-red-500' : 'border-primary-300'}`} />
                      </div>
                      {wrongKind && (
                        <div className="text-[11px] text-red-600 ml-24 pl-2">
                          That is a Payments offer — it cannot discount a subscription.
                        </div>)}
                    </div>
                  );
                })}
                <div className="text-[11px] text-grayscale-1100">
                  Fill Card and/or UPI to cover both — a Razorpay subscription offer covers one
                  method. Anything set there overrides &ldquo;Any method&rdquo;.
                </div>
                {rzOffersError && (
                  <div className="text-[11px] text-red-600">
                    Could not reach Razorpay to check offer ids: {rzOffersError}
                  </div>)}
              </div>
              {/* Who may redeem it. Both blank = anyone holding the code,
                  which is how every existing promotion behaves. */}
              <div className="border-t border-grayscale-200 pt-3">
                <div className="text-xs font-medium text-grayscale-1300 mb-1">
                  Who can redeem it <span className="font-normal text-grayscale-1100">
                  — leave blank for anyone with the code</span>
                </div>
                <div className="text-[11px] text-grayscale-1100 mb-1">
                  When either is set, an account we cannot identify is refused rather
                  than allowed.
                </div>
                <textarea rows={3}
                  placeholder="Allowed emails — one per line, or comma separated (blank = no email restriction)"
                  value={promoForm.eligible_emails || ''}
                  onChange={e => setPromoForm((f: any) => ({ ...f, eligible_emails: e.target.value }))}
                  className="w-full px-2.5 py-2 rounded-md border-[0.5px] border-primary-300 text-sm font-mono" />
                <div className="flex items-center gap-2 mt-2 flex-wrap min-w-0">
                  <span className="text-xs text-grayscale-1100">Only accounts created before</span>
                  <input type="date" value={promoForm.eligible_signup_before || ''}
                    onChange={e => setPromoForm((f: any) => ({ ...f, eligible_signup_before: e.target.value }))}
                    className="h-9 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm" />
                  <span className="text-[11px] text-grayscale-1100">(blank = any signup date)</span>
                </div>
              </div>
              <input placeholder="Max redemptions (blank = unlimited)" value={promoForm.max_redemptions}
                onChange={e => setPromoForm((f: any) => ({ ...f, max_redemptions: e.target.value }))}
                className="h-10 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm" />
              <div className="flex justify-end gap-2">
                <Btn subtle onClick={() => setPromoForm(null)}>Close</Btn>
                <Btn onClick={async () => {
                  const payload = { ...promoForm };
                  payload.max_redemptions = String(promoForm.max_redemptions).trim()
                    ? Number(promoForm.max_redemptions) : null;
                  // Explicit Z: parsing 'YYYY-MM-DDT00:00:00' as LOCAL time and
                  // re-slicing the UTC result walked the date a day per save
                  // for anyone east or west of UTC.
                  payload.starts_at = promoForm.starts_at
                    ? `${promoForm.starts_at}T00:00:00Z` : null;
                  payload.ends_at = promoForm.ends_at
                    ? `${promoForm.ends_at}T23:59:59Z` : null;
                  // Same explicit Z for the eligibility cutoff. A bare
                  // YYYY-MM-DD is interpreted in the DATABASE's timezone, so
                  // "created before 1 June" would mean a different instant
                  // there than it does here.
                  payload.eligible_signup_before = promoForm.eligible_signup_before
                    ? `${promoForm.eligible_signup_before}T00:00:00Z` : null;
                  const r = await apiService.adminBillingSavePromotion(userId, payload);
                  if (r.success && r.data?.success) { flash('Promotion saved'); setPromoForm(null); loadPromos(); }
                  else fail(r.error || 'Save failed');
                }}>Save</Btn>
              </div>
            </div>
          </div>
        )}

        {/* Refund modal */}
        {refund && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 flex flex-col gap-3">
              <h3 className="text-lg font-bold text-grayscale-1300 font-grotesk">Refund payment</h3>
              <div className="text-sm text-grayscale-1200">
                {refund.email}<br />
                <span className="font-mono text-xs text-grayscale-1100">{refund.payment_id}</span>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" checked={refund.full}
                  onChange={() => setRefund(r => r && ({ ...r, full: true }))} />
                Full refund ({money(refund.max_minor, refund.currency)})
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" checked={!refund.full}
                  onChange={() => setRefund(r => r && ({ ...r, full: false }))} />
                Partial
                <input type="number" disabled={refund.full}
                  value={refund.amount_minor / 100}
                  onChange={e => setRefund(r => r && ({ ...r, amount_minor: Math.round(Number(e.target.value) * 100) }))}
                  className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 text-sm w-28 disabled:opacity-40" />
                <span className="text-xs text-grayscale-1100">{refund.currency}</span>
              </label>
              {refund.full && (
                <label className="flex items-center gap-2 text-xs text-grayscale-1200">
                  <input type="checkbox" checked={refund.end_access}
                    onChange={e => setRefund(r => r && ({ ...r, end_access: e.target.checked }))} />
                  End their access now (also cancels auto-renewal)
                </label>
              )}
              <input placeholder="Reason (recorded on the payment)" value={refund.reason}
                onChange={e => setRefund(r => r && ({ ...r, reason: e.target.value }))}
                className="h-10 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm" />
              <div className="flex justify-end gap-2">
                <Btn subtle onClick={() => setRefund(null)}>Close</Btn>
                <Btn onClick={async () => {
                  if (!window.confirm(`Refund ${refund.full ? money(refund.max_minor, refund.currency)
                    : money(refund.amount_minor, refund.currency)} to ${refund.email}?`)) return;
                  const r = await apiService.adminBillingRefund(userId, {
                    payment_id: refund.payment_id,
                    amount_minor: refund.full ? null : refund.amount_minor,
                    reason: refund.reason, end_access: refund.end_access,
                  });
                  if (r.success && r.data?.success) {
                    flash(`Refunded — ${r.data.refund_id}${r.data.access_ended ? ', access ended' : ''}`);
                    setRefund(null); loadPayments();
                  } else fail(r.error || 'Refund failed');
                }}>Refund</Btn>
              </div>
            </div>
          </div>
        )}

        {/* Razorpay tab: stop a mandate by its gateway id. Immediate, not
            at cycle end — the button exists for the mandate no account
            reaches, and "at cycle end" is the answer that let the ghost
            charge happen. The reason gates the button: the cancellation row
            the server files is where "why" survives. */}
        {gwAction?.kind === 'cancel' && (() => {
          const a = gwAction;
          const r = a.sub;
          const payer = r.customer?.email || r.notes?.email || r.our_row?.email || r.customer_id;
          return (
            <Modal title="Cancel subscription at Razorpay" onClose={() => !a.busy && setGwAction(null)}>
              <div className="flex flex-col gap-3">
                <div className="text-sm text-grayscale-1200">
                  <div className="font-mono text-xs">{r.id}</div>
                  <div>{r.plan?.name || r.plan_id} · {payer || 'unknown payer'}</div>
                  <div className="text-xs text-grayscale-1100">
                    status {r.status}{r.charge_at ? ` · next charge ${fmtDate(r.charge_at)}` : ''}
                    {r.paid_count ? ` · ${r.paid_count} charge(s) so far` : ' · never charged'}
                  </div>
                  <div className="text-xs mt-1">Our account: {ourAccountCell(r.our_row, r.notes_user)}</div>
                </div>
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Stops the mandate <b>now</b>. Razorpay will not charge it again. Any row we
                  hold for it is closed and a cancellation is filed under your name; no e-mail
                  goes to the customer from here.
                </div>
                <input placeholder="Reason (required — filed on the cancellation)" value={a.reason}
                  autoFocus
                  onChange={e => setGwAction(x => x && x.kind === 'cancel' ? { ...x, reason: e.target.value } : x)}
                  className="h-10 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm" />
                <div className="flex justify-end gap-2">
                  <Btn subtle disabled={a.busy} onClick={() => setGwAction(null)}>Close</Btn>
                  <DangerBtn disabled={a.busy || !a.reason.trim()}
                    title={a.reason.trim() ? '' : 'Say why first'}
                    onConfirm={doGwCancel}>
                    {a.busy ? 'Cancelling…' : 'Cancel at Razorpay'}
                  </DangerBtn>
                </div>
              </div>
            </Modal>
          );
        })()}

        {/* Razorpay tab: refund by payment id. The dialog says up front
            whether we hold the payment — with a row it is the ordinary
            refund; without one the server refunds at the gateway directly
            and, on a full refund, cancels the mandate it was charged on. */}
        {gwAction?.kind === 'refund' && (() => {
          const a = gwAction;
          const amt = a.full ? a.max_minor : a.amount_minor;
          const amountBad = !a.full && (!Number.isFinite(amt) || amt < 100 || amt > a.max_minor);
          const canSend = !a.busy && !amountBad && a.reason.trim().length > 0 && a.max_minor > 0;
          return (
            <Modal title="Refund at Razorpay" onClose={() => !a.busy && setGwAction(null)}>
              <div className="flex flex-col gap-3">
                <div className="text-sm text-grayscale-1200">
                  <div>{a.payer}</div>
                  <div className="font-mono text-xs text-grayscale-1100">{a.payment_id}</div>
                  {a.subscription_id && <div className="font-mono text-xs text-grayscale-1100">on {a.subscription_id}</div>}
                  <div className="text-xs mt-1">
                    {a.ours
                      ? <>In our DB as <b>{a.ours}</b> — the usual refund, logged on their account.</>
                      : <span className="text-red-600">Not on any account of ours — refunded at the gateway directly, logged with no user.</span>}
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" checked={a.full}
                    onChange={() => setGwAction(x => x && x.kind === 'refund' ? { ...x, full: true } : x)} />
                  Full refund ({money(a.max_minor, a.currency)})
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" checked={!a.full}
                    onChange={() => setGwAction(x => x && x.kind === 'refund' ? { ...x, full: false } : x)} />
                  Partial
                  <input type="number" disabled={a.full}
                    value={a.amount_minor / 100}
                    onChange={e => setGwAction(x => x && x.kind === 'refund'
                      ? { ...x, amount_minor: Math.round(Number(e.target.value) * 100) } : x)}
                    className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 text-sm w-28 disabled:opacity-40" />
                  <span className="text-xs text-grayscale-1100">{a.currency}</span>
                </label>
                {amountBad && (
                  <div className="text-xs text-red-600">
                    Between {money(100, a.currency)} and {money(a.max_minor, a.currency)}.
                  </div>
                )}
                {a.full && (
                  <label className="flex items-center gap-2 text-xs text-grayscale-1200">
                    <input type="checkbox" checked={a.end_access}
                      onChange={e => setGwAction(x => x && x.kind === 'refund' ? { ...x, end_access: e.target.checked } : x)} />
                    End access and cancel the mandate at Razorpay (so it cannot charge again)
                  </label>
                )}
                <input placeholder="Reason (required — recorded on the refund)" value={a.reason}
                  autoFocus
                  onChange={e => setGwAction(x => x && x.kind === 'refund' ? { ...x, reason: e.target.value } : x)}
                  className="h-10 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm" />
                <div className="flex justify-end gap-2">
                  <Btn subtle disabled={a.busy} onClick={() => setGwAction(null)}>Close</Btn>
                  <DangerBtn disabled={!canSend}
                    title={a.reason.trim() ? '' : 'Say why first'}
                    onConfirm={doGwRefund}>
                    {a.busy ? 'Refunding…' : `Refund ${money(amt, a.currency)}`}
                  </DangerBtn>
                </div>
              </div>
            </Modal>
          );
        })()}

        {/* Refund from the Users tab. Differs from the Payments-tab dialog in
            two ways that are the point: the admin picks WHICH of the person's
            payments (they may hold a trial charge and a renewal), and the
            reason is mandatory — every refund we issue should say why from
            our side, because the log is what answers "why did ₹9,000 leave
            in September" six months on. */}
        {userRefund && (() => {
          const ur = userRefund;
          const sel = ur.payments.find((p: any) => p.razorpay_payment_id === ur.payment_id);
          const cur = sel?.currency || 'INR';
          const maxMinor = sel ? refundableMinor(sel) : 0;
          const amt = ur.full ? maxMinor : ur.amount_minor;
          const amountBad = !ur.full && (!Number.isFinite(amt) || amt < 100 || amt > maxMinor);
          const canSend = !!sel && !ur.busy && !amountBad && ur.reason.trim().length > 0;
          const pick = (pid: string) => {
            const p = ur.payments.find((x: any) => x.razorpay_payment_id === pid);
            setUserRefund(s => s && ({ ...s, payment_id: pid, amount_minor: p ? refundableMinor(p) : 0 }));
          };
          return (
            <Modal title={`Refund ${ur.user.email}`} onClose={() => !ur.busy && setUserRefund(null)}>
              {ur.loading ? (
                <div className="text-sm text-grayscale-1100">Loading their payments…</div>
              ) : !ur.payments.length ? (
                <div className="text-sm text-grayscale-1100">
                  No refundable payments — nothing captured and verified that has not
                  already been refunded in full.
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {ur.subscription && (
                    <div className="text-xs text-grayscale-1100">
                      Current plan: <b className="text-grayscale-1300">{ur.subscription.plan_code || '—'}</b>
                      {ur.subscription.status ? ` · ${ur.subscription.status}` : ''}
                      {ur.subscription.current_end ? ` · until ${fmtDate(ur.subscription.current_end)}` : ''}
                      {ur.subscription.auto_renew ? ' · auto-renews' : ''}
                    </div>
                  )}
                  <label className="text-xs flex flex-col gap-1">
                    <span className="text-grayscale-1100">Payment</span>
                    <select value={ur.payment_id} onChange={e => pick(e.target.value)}
                      className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm">
                      {ur.payments.map((p: any) => (
                        <option key={p.razorpay_payment_id} value={p.razorpay_payment_id}>
                          {fmtDate(p.created_at)} · {money(p.amount_paise, p.currency || 'INR')}
                          {' · '}{p.method || '—'}
                          {p.description ? ` · ${p.description}` : ''}
                          {Number(p.refunded_so_far || 0) > 0
                            ? ` (${money(p.refunded_so_far, p.currency || 'INR')} already refunded)` : ''}
                        </option>
                      ))}
                    </select>
                    {sel && (
                      <span className="font-mono text-[10px] text-grayscale-1100">
                        {sel.razorpay_payment_id}{sel.coupon_code ? ` · coupon ${sel.coupon_code}` : ''}
                      </span>
                    )}
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="radio" checked={ur.full}
                      onChange={() => setUserRefund(s => s && ({ ...s, full: true }))} />
                    Full refund ({money(maxMinor, cur)})
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="radio" checked={!ur.full}
                      onChange={() => setUserRefund(s => s && ({ ...s, full: false }))} />
                    Partial
                    <input type="number" disabled={ur.full} min={1}
                      value={ur.amount_minor / 100}
                      onChange={e => setUserRefund(s => s && ({
                        ...s, amount_minor: Math.round(Number(e.target.value) * 100) }))}
                      className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 text-sm w-28 disabled:opacity-40" />
                    <span className="text-xs text-grayscale-1100">{cur}</span>
                    {amountBad && (
                      <span className="text-xs text-red-600">1 to {money(maxMinor, cur)}</span>
                    )}
                  </label>
                  <label className="flex items-center gap-2 text-xs text-grayscale-1200">
                    <input type="checkbox" checked={ur.end_access}
                      onChange={e => setUserRefund(s => s && ({ ...s, end_access: e.target.checked }))} />
                    End access now (also cancels auto-renewal)
                    {/* The server only ends access on a full refund: a
                        partial one is a goodwill credit, not an exit. */}
                    {!ur.full && <span className="text-grayscale-1100">— applies to a full refund only</span>}
                  </label>
                  <label className="text-xs flex flex-col gap-1">
                    <span className="text-grayscale-1100">
                      Reason <span className="text-red-600">*</span> — recorded in the refunds log
                    </span>
                    <textarea value={ur.reason} rows={2} maxLength={500} autoFocus
                      placeholder="e.g. charged after cancelling the trial; duplicate mandate"
                      onChange={e => setUserRefund(s => s && ({ ...s, reason: e.target.value }))}
                      className="w-full px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm resize-none" />
                  </label>
                  <div className="flex justify-end items-center gap-2 pt-1">
                    <Btn subtle disabled={ur.busy} onClick={() => setUserRefund(null)}>Close</Btn>
                    <DangerBtn disabled={!canSend}
                      title={!ur.reason.trim() ? 'Say why before refunding' : ''}
                      onConfirm={async () => {
                        if (!sel) return;
                        setUserRefund(s => s && ({ ...s, busy: true }));
                        const r = await apiService.adminBillingRefundUser(userId, {
                          target_user_id: ur.user.user_id,
                          payment_id: sel.razorpay_payment_id,
                          amount_minor: ur.full ? null : ur.amount_minor,
                          reason: ur.reason.trim(), end_access: ur.end_access,
                        });
                        if (r.success && r.data?.success) {
                          flash(`Refunded ${money(r.data.amount ?? amt, cur)} to ${ur.user.email}`
                            + ` — Razorpay refund ${r.data.refund_id || '—'}`
                            + (r.data.access_ended ? ', access ended' : ''));
                          setUserRefund(null); loadUsers();
                        } else {
                          setUserRefund(s => s && ({ ...s, busy: false }));
                          fail(r.error || 'Refund failed');
                        }
                      }}>
                      {ur.busy ? 'Refunding…' : `Refund ${money(amt, cur)}`}
                    </DangerBtn>
                  </div>
                </div>
              )}
            </Modal>
          );
        })()}

        {/* Manual grant modal */}
        {grant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 flex flex-col gap-3">
              <h3 className="text-lg font-bold text-grayscale-1300 font-grotesk">Manual subscription</h3>
              <input placeholder="User email" value={grant.email}
                onChange={e => setGrant(g => g && ({ ...g, email: e.target.value }))}
                className="h-10 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm" />
              <div className="flex gap-2">
                <select value={grant.plan_code}
                  onChange={e => setGrant(g => g && ({ ...g, plan_code: e.target.value }))}
                  className="h-10 px-2 rounded-md border-[0.5px] border-primary-300 text-sm flex-1">
                  {(plans.length ? plans : [{ code: 'voyager' }, { code: 'navigator' }]).map((p: any) =>
                    <option key={p.code} value={p.code}>{p.code}</option>)}
                </select>
                <input type="number" min={1} max={120} value={grant.months}
                  onChange={e => setGrant(g => g && ({ ...g, months: Number(e.target.value) }))}
                  className="h-10 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm w-24" />
                <span className="self-center text-xs text-grayscale-1100">months</span>
              </div>
              <input placeholder="Note (why)" value={grant.note}
                onChange={e => setGrant(g => g && ({ ...g, note: e.target.value }))}
                className="h-10 px-2.5 rounded-md border-[0.5px] border-primary-300 text-sm" />
              <div className="flex justify-end gap-2">
                <Btn subtle onClick={() => setGrant(null)}>Close</Btn>
                <Btn onClick={async () => {
                  const r = await apiService.adminBillingManualSub(userId, grant);
                  if (r.success && r.data?.success) { flash(`Granted ${grant.plan_code} to ${grant.email}`); setGrant(null); loadUsers(); }
                  else fail(r.error || 'Grant failed');
                }}>Grant</Btn>
              </div>
            </div>
          </div>
        )}

        {countryFor && (
          <CountryPicker
            email={countryFor.email} current={countryFor.country || ''} countries={countries}
            onClose={() => setCountryFor(null)}
            onPick={async (country: string) => {
              const u = countryFor;
              setCountryFor(null);
              const r = await apiService.adminBillingSetCountry(userId, u.user_id, country.trim());
              if (r.success && r.data?.success) {
                flash(country
                  ? `${u.email}: ${r.data.billing_region} — quotes in ${r.data.currency_if_profile_used}`
                  : `${u.email}: override cleared — detected as ${r.data.billing_region}`);
                loadUsers();
              } else fail(r.error || 'Could not set the country');
            }} />
        )}

        {changePlanFor && (
          <ChangePlanModal userId={changePlanFor.user_id} email={changePlanFor.email}
            onClose={() => setChangePlanFor(null)}
            onDone={(res: any) => {
              const u = changePlanFor;
              setChangePlanFor(null);
              const said = describePlanChange(res);
              if (said.kind === 'ok') flash(`${u.email}: ${said.text}`);
              else fail(`${u.email}: ${said.text}`);
              loadUsers();
            }} />
        )}
      </div>
    </div>
  );
};

export default AdminBillingPage;
